import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Table, Card, Form, Badge, Modal, Alert } from "react-bootstrap";
import { useDispatch } from "react-redux";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  ArrowLeft,
  Move,
  Target,
  CheckCircle,
  MapPin,
  Trash2,
  Map as MapIcon,
  ZoomIn,
  ZoomOut,
  Upload,
  Globe,
  Layers,
  Search,
  X,
  Lock,
  Unlock,
  Scissors,
  RotateCcw,
  RotateCw,
  Check,
  FileText,
} from "lucide-react";
import Axios, { authHeaders, getProxiedImageUrl } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import { useLoader } from "../../context/LoaderContext";
import { georeferenceMapFile } from "../../redux/slice/mapSlice";
import VisualMapOverlayEditor from "./VisualMapOverlayEditor";
import {
  identityRecipe,
  rotateRecipe,
  cropRecipeWith,
  renderRecipe,
  isUsableRecipe,
  rotatePoints,
  cropPoints,
  pointsToBase,
  insidePolygon,
} from "./cropRecipe";
import Select from "react-select";
import { CheckboxOption, MultiValueMessage } from "../../components/form/utils";

const mapTypeSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "28px",
    height: "28px",
    fontSize: "11px",
    borderRadius: "6px",
    borderColor: "#cbd5e1",
    boxShadow: "none",
    padding: "0 2px",
    backgroundColor: "#ffffff",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "28px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
    padding: "0px",
    fontSize: "11px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "28px",
    display: "flex",
    alignItems: "center",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "0 4px",
    color: "#64748b",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "0 4px",
    color: "#ef4444",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e2e8f0",
    borderRadius: "4px",
    margin: "1px 2px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: "10px",
    padding: "1px 4px",
    color: "#1e293b",
  }),
  multiValueRemove: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "11px",
    minWidth: "200px",
  }),
};

// ═══ Web Mercator Projection Helpers ══════════════════════════════════════
const R_EARTH = 6378137;
const mX = (lng) => (R_EARTH * lng * Math.PI) / 180;
const mY = (lat) => R_EARTH * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const iX = (X) => (X / R_EARTH) * (180 / Math.PI);
const iY = (Y) => ((2 * Math.atan(Math.exp(Y / R_EARTH)) - Math.PI / 2) * 180) / Math.PI;
const groundK = (lat) => Math.cos((lat * Math.PI) / 180);

const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// ═══ 2x3 Matrix Helpers (Image px -> Mercator meters) ═════════════════════
const matApply = (m, x, y) => [m.a * x + m.c * y + m.e, m.b * x + m.d * y + m.f];

const getRotatedCornersFromXform = (m, w, h) => {
  if (!m || !w || !h) return [];
  const tl = matApply(m, 0, 0);
  const tr = matApply(m, w, 0);
  const br = matApply(m, w, h);
  const bl = matApply(m, 0, h);
  return [
    [parseFloat(iX(tl[0]).toFixed(6)), parseFloat(iY(tl[1]).toFixed(6))],
    [parseFloat(iX(tr[0]).toFixed(6)), parseFloat(iY(tr[1]).toFixed(6))],
    [parseFloat(iX(br[0]).toFixed(6)), parseFloat(iY(br[1]).toFixed(6))],
    [parseFloat(iX(bl[0]).toFixed(6)), parseFloat(iY(bl[1]).toFixed(6))],
  ];
};
// eslint-disable-next-line no-unused-vars
function matMul(m2, m1) {
  return {
    a: m2.a * m1.a + m2.c * m1.b,
    b: m2.b * m1.a + m2.d * m1.b,
    c: m2.a * m1.c + m2.c * m1.d,
    d: m2.b * m1.c + m2.d * m1.d,
    e: m2.a * m1.e + m2.c * m1.f + m2.e,
    f: m2.b * m1.e + m2.d * m1.f + m2.f,
  };
}
// eslint-disable-next-line no-unused-vars
const matRotAbout = (th, Px, Py) => {
  const cs = Math.cos(th),
    sn = Math.sin(th);
  return { a: cs, b: sn, c: -sn, d: cs, e: Px - cs * Px + sn * Py, f: Py - sn * Px - cs * Py };
};
// eslint-disable-next-line no-unused-vars
const matScaleAbout = (k, Px, Py) => ({ a: k, b: 0, c: 0, d: k, e: Px * (1 - k), f: Py * (1 - k) });
// eslint-disable-next-line no-unused-vars
const matTrans = (dx, dy) => ({ a: 1, b: 0, c: 0, d: 1, e: dx, f: dy });
// eslint-disable-next-line no-unused-vars
function matInv(m) {
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < 1e-18) return null;
  return {
    a: m.d / det,
    c: -m.c / det,
    e: (m.c * m.f - m.d * m.e) / det,
    b: -m.b / det,
    d: m.a / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

function decompose(m) {
  const sX = Math.hypot(m.a, m.b);
  const sY = Math.hypot(m.c, m.d);
  const rot = (Math.atan2(m.b, m.a) * 180) / Math.PI;
  return { scale: sX, scaleY: sY, rot: rot };
}

// ═══ Mathematical Least-Squares Solvers ═══════════════════════════════════
function fitSimilarity(pairs) {
  const n = pairs.length;
  if (n < 2) return null;
  let zx = 0, zy = 0, wx = 0, wy = 0;
  for (const p of pairs) {
    zx += p.x; zy += p.y; wx += p.X; wy += p.Y;
  }
  zx /= n; zy /= n; wx /= n; wy /= n;

  const build = (sgn) => {
    let nre = 0, nim = 0, den = 0;
    for (const p of pairs) {
      const dzx = p.x - zx, dzy = (p.y - zy) * sgn;
      const dwx = p.X - wx, dwy = p.Y - wy;
      nre += dwx * dzx + dwy * dzy;
      nim += dwy * dzx - dwx * dzy;
      den += dzx * dzx + dzy * dzy;
    }
    if (den === 0) return null;
    const ar = nre / den, ai = nim / den;
    const m = { a: ar, b: ai, c: -ai * sgn, d: ar * sgn, e: 0, f: 0 };
    m.e = wx - (m.a * zx + m.c * zy);
    m.f = wy - (m.b * zx + m.d * zy);
    return m;
  };

  const cands = [build(1), build(-1)].filter(Boolean);
  if (!cands.length) return null;
  let best = null;
  for (const m of cands) {
    let sse = 0;
    for (const p of pairs) {
      const q = matApply(m, p.x, p.y);
      sse += (q[0] - p.X) ** 2 + (q[1] - p.Y) ** 2;
    }
    if (!best || sse < best.sse) best = { m: m, sse: sse };
  }
  return best.m;
}

function fitAffine(pairs) {
  if (pairs.length < 3) return null;
  const solve = (key) => {
    const S = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], T = [0, 0, 0];
    for (const p of pairs) {
      const v = [p.x, p.y, 1];
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) S[i][j] += v[i] * v[j];
        T[i] += v[i] * p[key];
      }
    }
    const A = S.map((r, i) => [...r, T[i]]);
    for (let i = 0; i < 3; i++) {
      let piv = i;
      for (let q = i + 1; q < 3; q++) if (Math.abs(A[q][i]) > Math.abs(A[piv][i])) piv = q;
      if (Math.abs(A[piv][i]) < 1e-12) return null;
      [A[i], A[piv]] = [A[piv], A[i]];
      for (let q = 0; q < 3; q++) {
        if (q === i) continue;
        const fq = A[q][i] / A[i][i];
        for (let c = i; c < 4; c++) A[q][c] -= fq * A[i][c];
      }
    }
    return [A[0][3] / A[0][0], A[1][3] / A[1][1], A[2][3] / A[2][2]];
  };
  const rx = solve("X"), ry = solve("Y");
  if (!rx || !ry) return null;
  return { a: rx[0], c: rx[1], e: rx[2], b: ry[0], d: ry[1], f: ry[2] };
}

/** Widest the trace canvas backing store is rendered. See getTraceDisplayDimensions. */
const TRACE_CANVAS_MAX_PX = 6000;

/** With a 6000px backing store, zooming reveals real pixels rather than
 *  upscaling the same bitmap, so it is worth allowing more of it. */
const TRACE_ZOOM_MAX = 8;

/** The outer ring ([lng, lat] points) of a Polygon, or of a MultiPolygon's largest part. */
const outerRingOf = (geometry) => {
  if (!geometry) return null;
  if (geometry.type === "Polygon") return geometry.coordinates?.[0] || null;
  if (geometry.type !== "MultiPolygon") return null;
  let best = null;
  let bestArea = -1;
  for (const polygon of geometry.coordinates || []) {
    const ring = polygon[0] || [];
    let area = 0;
    for (let i = 0; i < ring.length - 1; i += 1) area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
    if (Math.abs(area) > bestArea) {
      bestArea = Math.abs(area);
      best = ring;
    }
  }
  return best;
};

/** The census code an imported sheet carries, wherever it is kept. */
const censusCodeOf = (detail) =>
  detail?.boundary?.census_code ||
  detail?.georefDraft?.import_meta?.census_code ||
  detail?.georeference?.import_meta?.census_code ||
  null;

/**
 * An imported village's name and census code, editable — for the clashes an
 * import couldn't settle (two villages claiming one census code or name).
 * Saves through PATCH /maps/map-files/:id/village, which updates every place
 * the two are kept and refuses a code another sheet already has.
 */
function VillageDetails({ detail, onSaved }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const currentCode = censusCodeOf(detail);
  const conflict = (detail?.georefDraft || detail?.georeference)?.import_meta?.census_code_conflict;

  const startEditing = () => {
    setTitle(detail.title || "");
    setCode(currentCode || "");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    if (title.trim() !== (detail.title || "")) body.title = title.trim();
    if (code.trim() !== (currentCode || "")) body.census_code = code.trim() || null;
    if (!Object.keys(body).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await Axios.patch(`/maps/map-files/${detail.id}/village`, body, authHeaders());
      onSaved(res.data?.data || {}, body);
      toast.success("Village details saved.");
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the village details.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="bg-white border rounded-3 px-3 py-2 mb-2 d-flex align-items-center flex-wrap gap-3 fs-xs">
        <span className="fw-semibold text-muted">Village</span>
        <span>
          <span className="text-muted">Name </span>
          <span className="fw-semibold">{detail.title || "-"}</span>
        </span>
        <span>
          <span className="text-muted">Census code </span>
          <span className="fw-semibold">{currentCode || "none"}</span>
          {!currentCode && conflict && (
            <span className="text-warning-emphasis"> · the package gave {conflict}, which another village also claims</span>
          )}
        </span>
        <Button size="sm" variant="outline-secondary" className="py-0 px-2 fs-xs ms-auto" onClick={startEditing}>
          Edit
        </Button>
      </div>
    );
  }

  return (
    <Form onSubmit={save} className="bg-white border rounded-3 px-3 py-2 mb-2 d-flex align-items-end flex-wrap gap-3 fs-xs">
      <Form.Group controlId="village-name" style={{ minWidth: 220 }}>
        <Form.Label className="mb-1 text-muted">Village name</Form.Label>
        <Form.Control size="sm" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} required />
      </Form.Group>
      <Form.Group controlId="village-census-code" style={{ minWidth: 200 }}>
        <Form.Label className="mb-1 text-muted">Census code</Form.Label>
        <Form.Control
          size="sm"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric"
          placeholder={conflict ? `e.g. ${conflict}` : "Digits; empty to clear"}
          maxLength={32}
        />
      </Form.Group>
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-secondary" className="fs-xs" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" type="submit" className="fs-xs" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
      <span className="text-muted" style={{ maxWidth: 360 }}>
        A census code belongs to one sheet. If another sheet has this village's code, clear it there first.
      </span>
    </Form>
  );
}

export default function GeoreferenceWorkspace() {
  const { mapCode, fileId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { setLoading } = useLoader();

  const [availableMapFiles, setAvailableMapFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(fileId || "");
  const [mapDetail, setMapDetail] = useState(null);
  const [alignmentMode, setAlignmentMode] = useState("visual"); // 'visual' | 'gcp'
  const [visualCorners, setVisualCorners] = useState([]);
  const [gcpPoints, setGcpPoints] = useState([]); // [{ x, y, lng, lat }]
  const [activeTabPoint, setActiveTabPoint] = useState({ x: null, y: null, lng: null, lat: null });
  const [mapStyle, setMapStyle] = useState("streets"); // 'streets' | 'satellite'
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [imageZoom, setImageZoom] = useState(1); // 1 = 100%, 2 = 200%, etc.
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ x: 0, y: 0 });

  // File upload & dragging state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const imageRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const zoomIntervalRef = useRef(null);

  // Location Search State for GCP Baseline Map
  const [gcpSearchQuery, setGcpSearchQuery] = useState("");
  const [isGcpSearching, setIsGcpSearching] = useState(false);

  // Nearby completed maps guides for GCP tab
  const [showGcpNearbyCompleted, setShowGcpNearbyCompleted] = useState(false);
  // The boundary an imported sheet came with, drawn on the GCP map.
  const [showBoundary, setShowBoundary] = useState(true);
  // Frame the map on an imported draft (or its boundary) once, when it opens.
  const draftFramePendingRef = useRef(false);
  const [gcpNearbyMaps, setGcpNearbyMaps] = useState([]);
  const [gcpMapTypes, setGcpMapTypes] = useState([]);
  const [gcpSelectedMapTypes, setGcpSelectedMapTypes] = useState([]);
  const isGcpDefaultSelectedRef = useRef(false);
  const gcpNearbySourcesRef = useRef(new Set());
  const gcpNearbyLayersRef = useRef(new Set());

  // Load map types on mount and default to active map sheet's type (e.g. TP) for GCP tab
  useEffect(() => {
    const fetchGcpMapTypesList = async () => {
      try {
        const res = await Axios.get("/maps/map-types/all?is_active=true&is_map_georeference=true", authHeaders());
        const list = res.data?.data || [];
        setGcpMapTypes(list);

        if (!isGcpDefaultSelectedRef.current) {
          let targetTypeId = null;

          if (mapDetail?.map_type_id || mapDetail?.mapType?.id) {
            targetTypeId = mapDetail?.map_type_id || mapDetail?.mapType?.id;
          } else if (selectedFileId && availableMapFiles.length > 0) {
            const found = availableMapFiles.find((f) => String(f.id) === String(selectedFileId));
            targetTypeId = found?.map_type_id || found?.mapType?.id;
          } else if (selectedFileId) {
            try {
              const fileRes = await Axios.get(`/maps/map-files/georeference/list?id=${selectedFileId}`, authHeaders());
              const fileRecord = fileRes.data?.data?.data?.find((f) => String(f.id) === String(selectedFileId)) || fileRes.data?.data?.data?.[0];
              targetTypeId = fileRecord?.map_type_id || fileRecord?.mapType?.id;
            } catch (err) {}
          }

          const matched = list.find(
            (t) =>
              (targetTypeId && String(t.id) === String(targetTypeId)) ||
              (mapCode && String(t.code).toLowerCase() === String(mapCode).toLowerCase()) ||
              (mapCode && String(t.name).toLowerCase() === String(mapCode).toLowerCase())
          );

          if (matched) {
            setGcpSelectedMapTypes([{ value: matched.id, label: matched.name }]);
            isGcpDefaultSelectedRef.current = true;
          } else if (list.length > 0) {
            setGcpSelectedMapTypes([{ value: list[0].id, label: list[0].name }]);
            isGcpDefaultSelectedRef.current = true;
          } else {
            setGcpSelectedMapTypes([]);
          }
        }
      } catch (err) {
        console.error("Failed to load GCP map types:", err);
      }
    };
    fetchGcpMapTypesList();
  }, [selectedFileId, availableMapFiles, mapDetail, mapCode]);

  // Purge GCP nearby maps state when selected map types filter changes
  useEffect(() => {
    setGcpNearbyMaps([]);
  }, [gcpSelectedMapTypes]);

  // Load nearby completed map sheets for GCP tab with BBOX spatial viewport querying
  useEffect(() => {
    if (!showGcpNearbyCompleted) {
      setGcpNearbyMaps([]);
      return;
    }

    const fetchGcpNearby = async () => {
      if (gcpMapTypes.length > 0 && (gcpSelectedMapTypes || []).length === 0) {
        setGcpNearbyMaps([]);
        return;
      }

      try {
        const mapTypeIds = (gcpSelectedMapTypes || []).map((t) => (typeof t === "object" ? t.value || t.id : t)).filter(Boolean).join(",");
        let url = "/maps/map-files/georeference/nearby?is_georeferenced=true";
        if (mapTypeIds) {
          url += `&map_type_id=${encodeURIComponent(mapTypeIds)}`;
        }
        if (selectedFileId) {
          url += `&exclude_id=${encodeURIComponent(selectedFileId)}`;
        }

        const map = mapRef.current;
        if (map && typeof map.getBounds === "function") {
          const bounds = map.getBounds();
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const lngPad = Math.abs(ne.lng - sw.lng) * 0.15;
          const latPad = Math.abs(ne.lat - sw.lat) * 0.15;
          const bbox = `${(sw.lng - lngPad).toFixed(6)},${(sw.lat - latPad).toFixed(6)},${(ne.lng + lngPad).toFixed(6)},${(ne.lat + latPad).toFixed(6)}`;
          url += `&bbox=${encodeURIComponent(bbox)}`;
        }

        const res = await Axios.get(url, authHeaders());
        const list = res.data?.data?.data || [];

        // Filter state to keep only maps matching current gcpSelectedMapTypes
        setGcpNearbyMaps((prev) => {
          const selectedTypeIds = (gcpSelectedMapTypes || []).map((t) => (typeof t === "object" ? String(t.value || t.id) : String(t))).filter(Boolean);
          const selectedSet = new Set(selectedTypeIds);

          const validPrev = (prev || []).filter((item) => {
            if (selectedSet.size === 0) return true;
            return selectedSet.has(String(item.map_type_id));
          });

          const mapDict = new Map(validPrev.map((item) => [item.id, item]));
          list.forEach((item) => {
            mapDict.set(item.id, item);
          });
          return Array.from(mapDict.values());
        });
      } catch (err) {
        console.error("Failed to load nearby completed map sheets for GCP tab:", err);
      }
    };

    fetchGcpNearby();

    const map = mapRef.current;
    if (!map) return;

    let timer = null;
    const handleMoveEnd = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fetchGcpNearby();
      }, 200);
    };

    map.on("moveend", handleMoveEnd);
    return () => {
      if (timer) clearTimeout(timer);
      map.off("moveend", handleMoveEnd);
    };
  }, [selectedFileId, gcpSelectedMapTypes, gcpMapTypes.length, showGcpNearbyCompleted, isMapLoaded]);

  // GCP Mathematical Fit & Live Overlay State
  const [xform, setXform] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [fitModel, setFitModel] = useState("aff");
  const [manualTouched, setManualTouched] = useState(false);
  const [gcpOpacity, setGcpOpacity] = useState(0.75);
  const [isFlickering, setIsFlickering] = useState(false);
  const [rmsStats, setRmsStats] = useState({ text: "no placement yet — click a point pair", status: "badge" });
  // eslint-disable-next-line no-unused-vars
  const [freeMode, setFreeMode] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Precision Loupe 10x Magnifier State & Ref
  const [loupeState, setLoupeState] = useState({ show: false, clientX: 0, clientY: 0, imgX: 0, imgY: 0 });
  const loupeCanvasRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const xhistRef = useRef([]);
  const overlayDataUrlRef = useRef(null);
  const currentTileUrlRef = useRef(null);

  const activeTabPointRef = useRef(activeTabPoint);
  activeTabPointRef.current = activeTabPoint;

  const gcpPointsRef = useRef(gcpPoints);
  gcpPointsRef.current = gcpPoints;

  const fitModelRef = useRef(fitModel);
  fitModelRef.current = fitModel;

  const solveGcpRef = useRef(null);
  const initialCenterDoneRef = useRef(false);

  const isLockedRef = useRef(isLocked);
  isLockedRef.current = isLocked;

  const xformRef = useRef(xform);
  xformRef.current = xform;

  // Trace Boundary / Crop Image State & Ref
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [tracePoints, setTracePoints] = useState([]);
  const [customCroppedSrc, setCustomCroppedSrc] = useState(null);
  // How the shown image was cut out of the original preview (see cropRecipe.js).
  // Sent instead of the cut-out pixels, so the backend can rebuild the crop
  // from the original PDF at full resolution. null = untouched, or unknown.
  const [cropState, setCropState] = useState(null);
  // "Start again from original PDF": the sheet was saved from an old browser
  // cut-out; this shows its original PDF instead, so it can be re-cropped and
  // saved from the PDF (sharp). See handleStartFromOriginal.
  const [useOriginal, setUseOriginal] = useState(false);
  const [showStartOriginal, setShowStartOriginal] = useState(false);
  const [startingOriginal, setStartingOriginal] = useState(false);
  // The preview size the carried points were computed for, checked on load.
  const expectedPreviewSizeRef = useRef(null);
  const [traceRotation, setTraceRotation] = useState(0);
  const [traceZoom, setTraceZoom] = useState(1);

  const [isTracePanning, setIsTracePanning] = useState(false);
  const traceCanvasRef = useRef(null);
  const traceContainerRef = useRef(null);

  const handleTraceZoomIn = () => {
    setTraceZoom((prev) => Math.min(TRACE_ZOOM_MAX, parseFloat((prev + 0.25).toFixed(2))));
  };

  const handleTraceZoomOut = () => {
    setTraceZoom((prev) => Math.max(1, parseFloat((prev - 0.25).toFixed(2))));
  };

  const handleTraceZoomReset = () => {
    setTraceZoom(1);
  };

  const handleRotateTrace = (delta) => {
    setTraceRotation((prev) => {
      let next = (prev + delta) % 360;
      if (next < 0) next += 360;
      return next;
    });
    setTracePoints([]);
  };

  const getTraceDisplayDimensions = useCallback(() => {
    const natW = imageRef.current?.naturalWidth || 1000;
    const natH = imageRef.current?.naturalHeight || 750;
    const isRotated90 = traceRotation === 90 || traceRotation === 270;
    const effW = isRotated90 ? natH : natW;
    const effH = isRotated90 ? natW : natH;

    // The canvas backing store, which is what actually limits how much of the
    // sheet you can see while tracing.
    //
    // This was 1600px. On a 12000px survey sheet that is a 7.5x downsample
    // before the image is ever displayed, which takes survey numbers from ~20px
    // to under 3px - unreadable, and no amount of zooming recovers it, because
    // traceZoom only scales the CSS size and upscales this same bitmap.
    //
    // 6000px is 36 megapixels (~144 MB), well within any desktop browser and far
    // below the full-size canvas that degrades. It also improves click
    // precision: points map back to source pixels by effW/canvasWidth, so this
    // takes a 12000px sheet from +/-7.5 source pixels per click to +/-2.
    const canvasWidth = Math.min(TRACE_CANVAS_MAX_PX, effW);
    const canvasHeight = Math.round((effH / effW) * canvasWidth);

    const container = traceContainerRef.current;
    const containerW = container ? Math.max(300, container.clientWidth - 40) : 880;
    const containerH = container ? Math.max(300, container.clientHeight - 40) : 520;

    const scale = Math.min(containerW / effW, containerH / effH);
    const fitW = Math.round(effW * scale);
    const fitH = Math.round(effH * scale);

    return {
      canvasWidth,
      canvasHeight,
      displayW: Math.round(fitW * traceZoom),
      displayH: Math.round(fitH * traceZoom),
    };
  }, [traceZoom, traceRotation]);

  const handleTraceContainerMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) { // Middle click OR Shift + Left click drag
      e.preventDefault();
      e.stopPropagation();
      const container = traceContainerRef.current;
      if (!container) return;

      setIsTracePanning(true);
      const startX = e.clientX;
      const startY = e.clientY;
      const startScrollLeft = container.scrollLeft;
      const startScrollTop = container.scrollTop;

      const onWindowMouseMove = (moveEvent) => {
        moveEvent.preventDefault();
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        container.scrollLeft = startScrollLeft - dx;
        container.scrollTop = startScrollTop - dy;
      };

      const onWindowMouseUp = (upEvent) => {
        setIsTracePanning(false);
        window.removeEventListener("mousemove", onWindowMouseMove);
        window.removeEventListener("mouseup", onWindowMouseUp);
      };

      window.addEventListener("mousemove", onWindowMouseMove);
      window.addEventListener("mouseup", onWindowMouseUp);
    }
  };

  const rawImageSrc =
    previewUrl ||
    (useOriginal ? mapDetail?.file_url : mapDetail?.georeference?.georef_original_url || mapDetail?.file_url) ||
    "";
  const baseImageSrc = previewUrl ? previewUrl : getProxiedImageUrl(rawImageSrc);
  const displayImageSrc = customCroppedSrc || baseImageSrc;

  // A recipe only means something against the original upload. When the shown
  // base is a cut-out saved the old way, or a newly uploaded image, the old
  // path is kept: the cut-out itself is uploaded.
  const savedOriginalUrl = mapDetail?.georeference?.georef_original_url;
  const recipeEligible =
    !uploadedFile &&
    !previewUrl &&
    (useOriginal || !savedOriginalUrl || savedOriginalUrl === mapDetail?.file_url);
  // Saved from an old browser cut-out of a PDF: it can start again from the PDF.
  const canStartFromOriginal =
    !useOriginal &&
    !uploadedFile &&
    !previewUrl &&
    /\.pdf$/i.test(mapDetail?.file_url || "") &&
    Boolean(savedOriginalUrl) &&
    savedOriginalUrl !== mapDetail?.file_url;

  // A sheet saved with a recipe shows its original preview, but its control
  // points are in the cut-out's pixels. Rebuild the cut-out from the recipe so
  // the points land where they were placed.
  const savedRecipe = mapDetail?.georeference?.crop_recipe;
  useEffect(() => {
    if (!recipeEligible || customCroppedSrc || !isUsableRecipe(savedRecipe) || !baseImageSrc) return;

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (cancelled) return;
      const [w, h] = savedRecipe.base_size;
      if (Math.abs(image.naturalWidth - w) > 1 || Math.abs(image.naturalHeight - h) > 1) {
        // The preview is not the image the crop was made on; showing the crop
        // on it would misplace everything, so say so instead.
        toast.warning("This sheet's saved crop no longer matches its preview. Crop it again before georeferencing.");
        return;
      }
      // The cut-out's own onLoad re-fits the points and redraws the overlay.
      // (A deferred refreshOverlay here would run this render's closure, from
      // before the fit existed, and remove the overlay instead.)
      setCustomCroppedSrc(renderRecipe(image, savedRecipe).toDataURL("image/png"));
      setCropState(savedRecipe);
      overlayDataUrlRef.current = null;
      currentTileUrlRef.current = null;
    };
    image.src = baseImageSrc;
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedRecipe, baseImageSrc, recipeEligible]);

  const handleGcpLocationSearch = async (e) => {
    if (e) e.preventDefault();
    const query = gcpSearchQuery.trim();
    if (!query || !mapRef.current) return;

    // 1. Lat, Lng coordinates match
    const coordMatch = query.match(/^(-?\d+(?:\.\d+)?)\s*[\s,]\s*(-?\d+(?:\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
        return;
      }
    }

    // 2. Geocoding place search
    try {
      setIsGcpSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
      );
      const results = await response.json();
      if (results && results.length > 0) {
        const place = results[0];
        const lng = parseFloat(place.lon);
        const lat = parseFloat(place.lat);
        mapRef.current.flyTo({ center: [lng, lat], zoom: 14 });
      } else {
        toast.error("No location found for: " + query);
      }
    } catch (err) {
      console.error("GCP location search failed:", err);
    } finally {
      setIsGcpSearching(false);
    }
  };

  const startContinuousZoom = (factor) => {
    stopContinuousZoom();
    setImageZoom((prev) => {
      const newZoom = Math.min(10.0, Math.max(0.05, parseFloat((prev * factor).toFixed(4))));
      return newZoom;
    });

    const stepFactor = factor > 1 ? 1.001 : 0.999;
    zoomIntervalRef.current = setInterval(() => {
      setImageZoom((prev) => {
        const newZoom = Math.min(10.0, Math.max(0.05, parseFloat((prev * stepFactor).toFixed(4))));
        return newZoom;
      });
    }, 40);
  };

  const stopContinuousZoom = () => {
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current);
      zoomIntervalRef.current = null;
    }
  };

  const handleResetImageZoom = () => {
    stopContinuousZoom();
    setImageZoom(1);
  };

  const imageContainerNodeRef = useRef(null);
  const imageContainerRef = useCallback((node) => {
    if (imageContainerNodeRef.current) {
      if (imageContainerNodeRef.current._onWheelNative) {
        imageContainerNodeRef.current.removeEventListener("wheel", imageContainerNodeRef.current._onWheelNative);
      }
    }
    imageContainerNodeRef.current = node;
    if (node) {
      const onWheelNative = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY < 0 ? 0.08 : -0.08;
        setImageZoom((prev) => {
          const newZoom = Math.min(4, Math.max(0.5, prev + delta));
          return parseFloat(newZoom.toFixed(2));
        });
      };
      node.addEventListener("wheel", onWheelNative, { passive: false });
      node._onWheelNative = onWheelNative;
    }
  }, []);

  // Fetch available map files for selector dropdown
  useEffect(() => {
    const fetchMapFilesList = async () => {
      try {
        const auth = authHeaders();
        const res = await Axios.get("/maps/map-files/georeference/list?limit=100", auth);
        const files = res.data?.data?.data || [];
        setAvailableMapFiles(files);
      } catch (err) {
        console.error("Error fetching map files list:", err);
      }
    };
    fetchMapFilesList();
  }, []);

  // Update selectedFileId when route fileId param changes
  useEffect(() => {
    if (fileId) {
      setSelectedFileId(fileId);
    }
  }, [fileId]);

  // Fetch map details when selectedFileId changes
  useEffect(() => {
    setImageZoom(1);
    setPanOffset({ x: 0, y: 0 });
    // A crop belongs to one sheet; never carry it over to the next.
    setCustomCroppedSrc(null);
    setCropState(null);
    setUseOriginal(false);
    expectedPreviewSizeRef.current = null;
    if (!selectedFileId) {
      setMapDetail(null);
      setGcpPoints([]);
      setVisualCorners([]);
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const auth = authHeaders();
        const res = await Axios.get(`/maps/map-files/georeference/list?id=${selectedFileId}`, auth);
        const record = res.data?.data?.data?.[0] || null;
        if (record) {
          setMapDetail(record);
          let gcpList = record.georeference?.gcp_points;
          let initialCorners = record.georeference?.corners && Array.isArray(record.georeference.corners)
            ? record.georeference.corners
            : [];

          // An imported sheet not on the map yet: start from the position the
          // package suggested, on satellite with the neighbouring sheets
          // shown, ready to check. Its points are marked imported: they follow
          // the sheet when it is moved by hand, and can be dropped once the
          // admin has placed points of their own.
          const draft = !record.georeference ? record.georefDraft : null;
          if (draft) {
            gcpList = Array.isArray(draft.gcp_points) ? draft.gcp_points.map((pt) => ({ ...pt, imported: true })) : [];
            initialCorners = Array.isArray(draft.corners) ? draft.corners : [];
            setAlignmentMode("gcp");
            setMapStyle("satellite");
            setShowGcpNearbyCompleted(true);
            setShowBoundary(true);
            initialCenterDoneRef.current = true; // framed below instead
            draftFramePendingRef.current = true;
          }

          setGcpPoints(
            gcpList && Array.isArray(gcpList)
              ? gcpList
              : []
          );
          setVisualCorners(initialCorners);
          if (gcpList && Array.isArray(gcpList) && gcpList.length >= 3) {
            setTimeout(() => {
              solveGcp(gcpList, "aff", true);
            }, 300);
          }
        } else {
          toast.error("Map file not found.");
        }
      } catch (err) {
        console.error("Error fetching map file details:", err);
        toast.error("Failed to load map file details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileId, setLoading, toast]);

  // Reset zoom and pan on alignment mode change
  useEffect(() => {
    setImageZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, [alignmentMode]);

  const handleSelectMapSheet = (newFileId) => {
    setSelectedFileId(newFileId);
    const selectedRecord = availableMapFiles.find((f) => String(f.id) === String(newFileId));
    if (selectedRecord) {
      const code = selectedRecord.mapType?.code?.toLowerCase() || mapCode || "dp";
      navigate(`/maps/${code}/georeference/${newFileId}`);
    }
  };

  // Clean up object URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Initialize MapLibre Map when in GCP mode
  useEffect(() => {
    if (alignmentMode !== "gcp") return;
    if (!mapContainerRef.current || mapRef.current) return;

    console.log("Initializing MapLibre GL Map for Georeference Workspace...");
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [72.5099, 23.04],
      zoom: 12,
      dragRotate: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      setIsMapLoaded(true);
    });

    map.on("click", (e) => {
      if (isLockedRef.current) {
        toast.info("Overlay position is locked. Unlock to add control points.");
        return;
      }

      const activePt = activeTabPointRef.current;
      const currentGcps = gcpPointsRef.current;

      if (activePt.x === null || activePt.y === null) {
        toast.info("Click a feature on the source image first, then click the matching spot on the map.");
        return;
      }

      const newPair = {
        x: activePt.x,
        y: activePt.y,
        lng: parseFloat(e.lngLat.lng.toFixed(6)),
        lat: parseFloat(e.lngLat.lat.toFixed(6)),
      };

      const updatedList = [...currentGcps, newPair];
      setGcpPoints(updatedList);
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
      solveGcp(updatedList, fitModel, true);
      toast.success(`Control Pair #${updatedList.length} added!`);
    });

    mapRef.current = map;

    // Trigger container resize after DOM render
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setIsMapLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alignmentMode]);

  // Handle satellite basemap layer switching in GCP mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || !map.isStyleLoaded()) return;

    const satSourceId = "satellite-tile-source";
    const satLayerId = "satellite-tile-layer";

    try {
      if (!map.getSource(satSourceId)) {
        map.addSource(satSourceId, {
          type: "raster",
          tiles: [
            "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          ],
          tileSize: 256,
          maxzoom: 19,
          attribution: "Esri, Maxar, Earthstar Geographics",
        });
      }

      if (!map.getLayer(satLayerId)) {
        map.addLayer({
          id: satLayerId,
          type: "raster",
          source: satSourceId,
          layout: {
            visibility: mapStyle === "satellite" ? "visible" : "none",
          },
        });
      } else {
        map.setLayoutProperty(
          satLayerId,
          "visibility",
          mapStyle === "satellite" ? "visible" : "none"
        );
      }
    } catch (err) {
      console.warn("MapLibre satellite layer waiting for style load in GCP mode:", err.message);
    }
  }, [mapStyle, isMapLoaded]);

  // Update map markers when GCP list changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    gcpPoints.forEach((pt, index) => {
      if (pt.lng !== null && pt.lat !== null) {
        const el = document.createElement("div");
        el.className = "gcp-marker-pin";
        el.style.cssText = `
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${pt.bad ? "#ef4444" : "#6366f1"};
          border: 2px solid #fff;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          cursor: default;
          user-select: none;
        `;
        el.innerText = String(index + 1);

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([pt.lng, pt.lat])
          .addTo(map);

        markersRef.current.push(marker);
      }
    });

    if (gcpPoints.length > 0 && gcpPoints[0].lng && !initialCenterDoneRef.current) {
      map.setCenter([gcpPoints[0].lng, gcpPoints[0].lat]);
      initialCenterDoneRef.current = true;
    }
  }, [gcpPoints]);

  // ═══ Precision Loupe Magnifier Pointer Movement ════════════════════════
  const handleImagePointerMove = (e) => {
    if (!imageRef.current || !loupeCanvasRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const pX = e.clientX - rect.left;
    const pY = e.clientY - rect.top;

    if (pX < 0 || pY < 0 || pX > rect.width || pY > rect.height) {
      setLoupeState((prev) => ({ ...prev, show: false }));
      return;
    }

    const naturalWidth = imageRef.current.naturalWidth || 1;
    const naturalHeight = imageRef.current.naturalHeight || 1;
    const scaleX = naturalWidth / rect.width;
    const scaleY = naturalHeight / rect.height;
    const imgX = pX * scaleX;
    const imgY = pY * scaleY;

    setLoupeState({
      show: true,
      clientX: e.clientX,
      clientY: e.clientY,
      imgX,
      imgY,
    });

    const loupeCv = loupeCanvasRef.current;
    const ctx = loupeCv.getContext("2d");
    if (!ctx) return;

    const LOUPE_R = 75;
    const LOUPE_MAG = 1.0;
    const srcSize = LOUPE_R / LOUPE_MAG;

    ctx.save();
    ctx.clearRect(0, 0, 150, 150);
    ctx.beginPath();
    ctx.arc(75, 75, 74, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 150, 150);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const sx = imgX - srcSize;
    const sy = imgY - srcSize;
    ctx.drawImage(imageRef.current, sx, sy, srcSize * 2, srcSize * 2, 0, 0, 150, 150);
    ctx.restore();

    ctx.strokeStyle = "rgba(226, 75, 74, 0.9)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(75, 58); ctx.lineTo(75, 92);
    ctx.moveTo(58, 75); ctx.lineTo(92, 75);
    ctx.stroke();
  };

  const handleImagePointerLeave = () => {
    setLoupeState((prev) => ({ ...prev, show: false }));
  };

  const handleImageClick = (e) => {
    if (isLocked) {
      toast.info("Overlay position is locked. Unlock to add control points.");
      return;
    }
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const naturalWidth = imageRef.current.naturalWidth || 1;
    const naturalHeight = imageRef.current.naturalHeight || 1;

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const scaleX = naturalWidth / rect.width;
    const scaleY = naturalHeight / rect.height;

    const imgX = Math.round(clickX * scaleX);
    const imgY = Math.round(clickY * scaleY);

    setActiveTabPoint((prev) => ({
      ...prev,
      x: imgX,
      y: imgY,
    }));
  };

  // ═══ Mathematical Solve & Live Overlay Preview ══════════════════════════
  const getOverlayDataUrl = useCallback(() => {
    const img = imageRef.current;
    if (!img || !img.complete || !img.naturalWidth) return displayImageSrc;
    if (overlayDataUrlRef.current) return overlayDataUrlRef.current;

    try {
      const cap = 1400;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const k = Math.min(1, cap / Math.max(w, h));

      const cv = document.createElement("canvas");
      cv.width = Math.max(1, Math.round(w * k));
      cv.height = Math.max(1, Math.round(h * k));
      const ctx = cv.getContext("2d");
      if (!ctx) return displayImageSrc;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, cv.width, cv.height);

      const dataUrl = cv.toDataURL("image/png");
      if (dataUrl && dataUrl.length > 100) {
        overlayDataUrlRef.current = dataUrl;
      }
      return dataUrl || displayImageSrc;
    } catch (e) {
      console.warn("Failed to generate overlay canvas data URL:", e);
      return displayImageSrc;
    }
  }, [displayImageSrc]);

  const refreshOverlay = useCallback(
    (m = xform) => {
      const map = mapRef.current;
      if (!map) return;

      if (!m) {
        try {
          if (map.getLayer("gcp-live-ovl-layer")) map.removeLayer("gcp-live-ovl-layer");
          if (map.getSource("gcp-live-ovl")) map.removeSource("gcp-live-ovl");
          currentTileUrlRef.current = null;
        } catch (err) { }
        return;
      }

      if (!imageRef.current) return;

      const w = imageRef.current.naturalWidth || 1000;
      const h = imageRef.current.naturalHeight || 1000;

      const coords = [
        [0, 0],
        [w, 0],
        [w, h],
        [0, h],
      ].map(([x, y]) => {
        const q = matApply(m, x, y);
        return [iX(q[0]), iY(q[1])];
      });

      const tileUrl = getOverlayDataUrl();
      if (!tileUrl) return;

      try {
        const src = map.getSource("gcp-live-ovl");
        const urlChanged = currentTileUrlRef.current !== tileUrl;

        if (src && !urlChanged) {
          src.setCoordinates(coords);
        } else {
          if (map.getLayer("gcp-live-ovl-layer")) map.removeLayer("gcp-live-ovl-layer");
          if (map.getSource("gcp-live-ovl")) map.removeSource("gcp-live-ovl");

          map.addSource("gcp-live-ovl", {
            type: "image",
            url: tileUrl,
            coordinates: coords,
          });
          map.addLayer(
            {
              id: "gcp-live-ovl-layer",
              type: "raster",
              source: "gcp-live-ovl",
              paint: {
                "raster-opacity": isFlickering ? 0 : Number(gcpOpacity),
                "raster-fade-duration": 0,
              },
            },
            // The imported boundary stays on top of the sheet.
            map.getLayer("import-boundary-line") ? "import-boundary-line" : undefined
          );
          currentTileUrlRef.current = tileUrl;
        }
        if (map.getLayer("gcp-live-ovl-layer")) {
          map.setPaintProperty("gcp-live-ovl-layer", "raster-opacity", isFlickering ? 0 : Number(gcpOpacity));
        }
      } catch (err) {
        console.warn("Error refreshing GCP live overlay:", err);
      }
    },
    [xform, getOverlayDataUrl, isFlickering, gcpOpacity]
  );

  // Render nearby completed map sheets on GCP Baseline Map with Incremental Layer Diffing (Zero-Blink)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || alignmentMode !== "gcp") return;

    const cleanupGcpNearbyLayers = () => {
      gcpNearbyLayersRef.current.forEach((layerId) => {
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        } catch (e) {}
      });
      gcpNearbyLayersRef.current.clear();

      gcpNearbySourcesRef.current.forEach((sourceId) => {
        try {
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        } catch (e) {}
      });
      gcpNearbySourcesRef.current.clear();
    };

    if (!showGcpNearbyCompleted) {
      cleanupGcpNearbyLayers();
      return;
    }

    // Purge any active Mapbox layers whose map item is no longer inside gcpNearbyMaps
    const activeGcpNearbyMapIds = new Set((gcpNearbyMaps || []).map((m) => String(m.id)));
    const layersToPurge = [];
    gcpNearbyLayersRef.current.forEach((layerId) => {
      const mapId = layerId.replace("gcp-nearby-layer-", "");
      if (!activeGcpNearbyMapIds.has(mapId)) {
        layersToPurge.push(layerId);
      }
    });

    layersToPurge.forEach((layerId) => {
      const sourceId = layerId.replace("gcp-nearby-layer-", "gcp-nearby-source-");
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      } catch (e) {}
      gcpNearbyLayersRef.current.delete(layerId);

      try {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch (e) {}
      gcpNearbySourcesRef.current.delete(sourceId);
    });

    if (gcpNearbyMaps.length > 0) {
      gcpNearbyMaps.forEach((item) => {
        const isCurrent = String(item.id) === String(selectedFileId);
        if (isCurrent) return;

        const georef = item.georeference;
        const cornersData = georef?.corners;
        if (!cornersData || !Array.isArray(cornersData) || cornersData.length !== 4) return;

        const sourceId = `gcp-nearby-source-${item.id}`;
        const layerId = `gcp-nearby-layer-${item.id}`;

        // If source & layer already exist, leave them untouched to prevent flickering
        if (map.getSource(sourceId) && map.getLayer(layerId)) return;

        const lngs = cornersData.map((c) => c[0]);
        const lats = cornersData.map((c) => c[1]);
        const flatBounds = [
          Math.min(...lngs),
          Math.min(...lats),
          Math.max(...lngs),
          Math.max(...lats),
        ];

        const tiledFileUrl = georef?.tiled_file_url;
        const warpedFileUrl = georef?.warped_file_url;

        try {
          if (!map.getSource(sourceId)) {
            if (tiledFileUrl) {
              const cleanTiledFileUrl = tiledFileUrl.split("?")[0];
              const baseUrl = cleanTiledFileUrl.split("/{z}")[0];
              const extension = cleanTiledFileUrl.substring(cleanTiledFileUrl.lastIndexOf("."));
              const proxiedTileUrl = `${getProxiedImageUrl(baseUrl)}/{z}/{x}/{y}${extension}`;

              map.addSource(sourceId, {
                type: "raster",
                tiles: [proxiedTileUrl],
                tileSize: 256,
                bounds: flatBounds,
                minzoom: 0,
                maxzoom: 22,
              });
            } else if (warpedFileUrl) {
              const uprightCorners = [
                [flatBounds[0], flatBounds[3]], // Top-Left
                [flatBounds[2], flatBounds[3]], // Top-Right
                [flatBounds[2], flatBounds[1]], // Bottom-Right
                [flatBounds[0], flatBounds[1]], // Bottom-Left
              ];
              map.addSource(sourceId, {
                type: "image",
                url: getProxiedImageUrl(warpedFileUrl),
                coordinates: uprightCorners,
              });
            } else {
              const rawUrl = item.file_url || georef?.georef_original_url;
              map.addSource(sourceId, {
                type: "image",
                url: getProxiedImageUrl(rawUrl),
                coordinates: cornersData,
              });
            }
            gcpNearbySourcesRef.current.add(sourceId);
          }

          if (!map.getLayer(layerId)) {
            const beforeLayer = map.getLayer("gcp-live-ovl-layer")
              ? "gcp-live-ovl-layer"
              : undefined;
            map.addLayer(
              {
                id: layerId,
                type: "raster",
                source: sourceId,
                paint: {
                  "raster-opacity": isFlickering ? 0 : Number(gcpOpacity),
                  "raster-fade-duration": 300,
                },
              },
              beforeLayer
            );
            gcpNearbyLayersRef.current.add(layerId);
          }
        } catch (err) {
          console.warn("Error rendering GCP nearby completed map overlay:", err);
        }
      });
    }
  }, [isMapLoaded, showGcpNearbyCompleted, gcpNearbyMaps, selectedFileId, alignmentMode, isFlickering, gcpOpacity, mapStyle]);

  // ═══ Trace Image & Boundary Cropping Logic ═════════════════════════════
  const drawTraceCanvas = useCallback(() => {
    const cv = traceCanvasRef.current;
    if (!cv || !imageRef.current) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    const img = imageRef.current;
    const w = cv.width;
    const h = cv.height;

    ctx.clearRect(0, 0, w, h);
    ctx.save();
    if (traceRotation === 90) {
      ctx.translate(w, 0);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, 0, 0, h, w);
    } else if (traceRotation === 180) {
      ctx.translate(w, h);
      ctx.rotate(Math.PI);
      ctx.drawImage(img, 0, 0, w, h);
    } else if (traceRotation === 270) {
      ctx.translate(0, h);
      ctx.rotate((270 * Math.PI) / 180);
      ctx.drawImage(img, 0, 0, h, w);
    } else {
      ctx.drawImage(img, 0, 0, w, h);
    }
    ctx.restore();

    if (tracePoints.length > 0) {
      // Draw semi-transparent polygon fill
      ctx.fillStyle = "rgba(99, 102, 241, 0.25)";
      ctx.strokeStyle = "#6366f1";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      ctx.beginPath();
      ctx.moveTo(tracePoints[0].x, tracePoints[0].y);
      for (let i = 1; i < tracePoints.length; i++) {
        ctx.lineTo(tracePoints[i].x, tracePoints[i].y);
      }
      if (tracePoints.length >= 3) {
        ctx.closePath();
        ctx.fill();
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw point markers
      tracePoints.forEach((pt, idx) => {
        ctx.fillStyle = "#ef4444";
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 10px sans-serif";
        ctx.fillText(String(idx + 1), pt.x + 7, pt.y - 7);
      });
    }
  }, [tracePoints, traceRotation]);

  useEffect(() => {
    if (showTraceModal) {
      setTimeout(() => drawTraceCanvas(), 50);
    }
  }, [showTraceModal, traceZoom, traceRotation, drawTraceCanvas]);

  // Non-passive mouse wheel zoom handler for trace modal container
  useEffect(() => {
    const el = traceContainerRef.current;
    if (!el || !showTraceModal) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY < 0 ? 0.15 : -0.15;
      setTraceZoom((prev) => Math.min(4, Math.max(1, parseFloat((prev + delta).toFixed(2)))));
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [showTraceModal]);

  const handleTraceCanvasClick = (e) => {
    if (e.shiftKey) return; // Prevent placing a trace point when Shift key was used for drag panning

    const cv = traceCanvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const scaleX = cv.width / rect.width;
    const scaleY = cv.height / rect.height;

    const ptX = Math.round(clickX * scaleX);
    const ptY = Math.round(clickY * scaleY);

    setTracePoints((prev) => [...prev, { x: ptX, y: ptY }]);
  };

  const handleApplyTraceMask = () => {
    const img = imageRef.current;
    const cv = traceCanvasRef.current;
    if (!img) return;

    const natW = img.naturalWidth || 1000;
    const natH = img.naturalHeight || 1000;
    const isRotated90 = traceRotation === 90 || traceRotation === 270;
    const effNatW = isRotated90 ? natH : natW;
    const effNatH = isRotated90 ? natW : natH;

    const off = document.createElement("canvas");
    off.width = effNatW;
    off.height = effNatH;
    const oc = off.getContext("2d");
    oc.imageSmoothingEnabled = true;
    oc.imageSmoothingQuality = "high";

    oc.save();
    if (traceRotation === 90) {
      oc.translate(effNatW, 0);
      oc.rotate((90 * Math.PI) / 180);
      oc.drawImage(img, 0, 0, natW, natH);
    } else if (traceRotation === 180) {
      oc.translate(effNatW, effNatH);
      oc.rotate(Math.PI);
      oc.drawImage(img, 0, 0, natW, natH);
    } else if (traceRotation === 270) {
      oc.translate(0, effNatH);
      oc.rotate((270 * Math.PI) / 180);
      oc.drawImage(img, 0, 0, natW, natH);
    } else {
      oc.drawImage(img, 0, 0, natW, natH);
    }
    oc.restore();

    // Case 1: Traced polygon provided -> Crop boundary with rotation
    if (tracePoints.length >= 3 && cv) {
      const scaleX = effNatW / cv.width;
      const scaleY = effNatH / cv.height;
      const realPts = tracePoints.map((p) => ({ x: p.x * scaleX, y: p.y * scaleY }));
      cropToPolygon({ off, oc, realPts, rotation: traceRotation, points: gcpPoints });
      return;
    }

    // Case 2: No trace points -> Apply whole-image rotation
    if (traceRotation !== 0) {
      const dataUrl = off.toDataURL("image/png");
      const currentRecipe = cropState || (customCroppedSrc ? null : identityRecipe(natW, natH));
      setCropState(currentRecipe ? rotateRecipe(currentRecipe, traceRotation) : null);
      setCustomCroppedSrc(dataUrl);
      setTracePoints([]);
      setTraceRotation(0);
      setGcpPoints(rotatePoints(gcpPoints, natW, natH, traceRotation));
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
      setXform(null);
      refreshOverlay(null);
      overlayDataUrlRef.current = null;
      currentTileUrlRef.current = null;
      setShowTraceModal(false);
      toast.success("Sheet rotated successfully!");
      return;
    }

    toast.error("Please click at least 3 boundary points to crop, or rotate the sheet.");
  };

  /**
   * Cuts the shown image to a polygon (in the pixels of the image as drawn on
   * `off`, i.e. after `rotation`): masks it, trims to the polygon's box,
   * records the crop in the recipe, and moves `points` with it (points
   * outside are dropped). Shared by the traced crop and Trim to boundary.
   */
  const cropToPolygon = ({ off, oc, realPts, rotation, points, message }) => {
    const img = imageRef.current;
    const natW = img.naturalWidth || 1000;
    const natH = img.naturalHeight || 1000;
    const effNatW = off.width;
    const effNatH = off.height;
    {
      oc.globalCompositeOperation = "destination-in";
      oc.beginPath();
      oc.moveTo(realPts[0].x, realPts[0].y);
      for (let i = 1; i < realPts.length; i++) {
        oc.lineTo(realPts[i].x, realPts[i].y);
      }
      oc.closePath();
      oc.fill();

      const xs = realPts.map((p) => p.x);
      const ys = realPts.map((p) => p.y);
      const pad = 10;
      const minX = Math.max(0, Math.min(...xs) - pad);
      const minY = Math.max(0, Math.min(...ys) - pad);
      const maxX = Math.min(effNatW, Math.max(...xs) + pad);
      const maxY = Math.min(effNatH, Math.max(...ys) + pad);
      const cropW = Math.max(10, maxX - minX);
      const cropH = Math.max(10, maxY - minY);

      const trimmed = document.createElement("canvas");
      trimmed.width = cropW;
      trimmed.height = cropH;
      const tc = trimmed.getContext("2d");
      tc.imageSmoothingEnabled = true;
      tc.imageSmoothingQuality = "high";
      tc.drawImage(off, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

      const dataUrl = trimmed.toDataURL("image/png");
      // The recipe follows the pixels. With no recipe yet, the shown image is
      // the original preview only if nothing has been cut out of it before.
      const currentRecipe = cropState || (customCroppedSrc ? null : identityRecipe(natW, natH));
      setCropState(
        currentRecipe
          ? cropRecipeWith(currentRecipe, rotation, realPts.map((p) => [p.x, p.y]), [minX, minY, cropW, cropH])
          : null
      );
      setCustomCroppedSrc(dataUrl);
      setTracePoints([]);
      setTraceRotation(0);
      // The points move with the crop; the cut-out's onLoad re-fits them.
      const keptPoints = cropPoints(
        rotatePoints(points, natW, natH, rotation),
        realPts.map((p) => [p.x, p.y]),
        [minX, minY, cropW, cropH]
      );
      setGcpPoints(keptPoints);
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
      setXform(null);
      refreshOverlay(null);
      overlayDataUrlRef.current = null;
      currentTileUrlRef.current = null;
      setShowTraceModal(false);
      const dropped = points.length - keptPoints.length;
      toast.success(
        dropped > 0
          ? `${message || "Boundary cropped."} ${dropped} control point(s) outside the crop were removed.`
          : message || "Boundary cropped with rotation successfully!"
      );
    }
  };

  /**
   * Trims an imported sheet to its village boundary: the boundary is carried
   * onto the image through the sheet's current placement, so this belongs
   * after the placement has been checked. Imported points (which only stand
   * for the placement) are first re-made inside the boundary, so the crop
   * keeps them; the admin's own points outside it are dropped as usual.
   */
  const handleTrimToBoundary = () => {
    const img = imageRef.current;
    const ring = outerRingOf(mapDetail?.boundary?.geometry);
    if (!img || !ring) return;
    if (customCroppedSrc) {
      toast.info("This sheet is already cropped. Reset the crop first to trim it to the boundary.");
      return;
    }
    const inv = xform ? matInv(xform) : null;
    if (!inv) {
      toast.info("Place the sheet first; the boundary is carried onto the image through its position.");
      return;
    }
    const w = img.naturalWidth || 1000;
    const h = img.naturalHeight || 1000;
    const open = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring.slice(0, -1) : ring;
    const step = Math.max(1, Math.ceil(open.length / 500)); // the server takes at most 500 points
    const poly = open
      .filter((_, i) => i % step === 0)
      .map(([lng, lat]) => {
        const q = matApply(inv, mX(lng), mY(lat));
        return [Math.min(w, Math.max(0, q[0])), Math.min(h, Math.max(0, q[1]))];
      });
    const xs = poly.map((p) => p[0]);
    const ys = poly.map((p) => p[1]);
    if (poly.length < 3 || Math.max(...xs) - Math.min(...xs) < 10 || Math.max(...ys) - Math.min(...ys) < 10) {
      toast.error("The boundary doesn't fall on this image at its current position.");
      return;
    }

    // Imported points re-made at well-spread spots inside the boundary.
    let points = gcpPoints;
    if (gcpPoints.some((pt) => pt.imported)) {
      const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      const inside = [];
      for (let i = 1; i < 24; i += 1) {
        for (let j = 1; j < 24; j += 1) {
          const x = x0 + ((x1 - x0) * i) / 24;
          const y = y0 + ((y1 - y0) * j) / 24;
          if (insidePolygon(x, y, poly)) inside.push([x, y]);
        }
      }
      if (inside.length >= 3) {
        const cx = inside.reduce((s, p) => s + p[0], 0) / inside.length;
        const cy = inside.reduce((s, p) => s + p[1], 0) / inside.length;
        const pick = (score) => inside.reduce((best, p) => (score(p) > score(best) ? p : best), inside[0]);
        const chosen = [
          pick((p) => p[0] + p[1]),
          pick((p) => p[0] - p[1]),
          pick((p) => -p[0] + p[1]),
          pick((p) => -p[0] - p[1]),
          pick((p) => -Math.hypot(p[0] - cx, p[1] - cy)),
        ].filter((p, i, all) => all.findIndex((o) => o[0] === p[0] && o[1] === p[1]) === i);
        const remade = chosen.map(([x, y]) => {
          const q = matApply(xform, x, y);
          return {
            x: Math.round(x * 100) / 100,
            y: Math.round(y * 100) / 100,
            lng: parseFloat(iX(q[0]).toFixed(8)),
            lat: parseFloat(iY(q[1]).toFixed(8)),
            imported: true,
          };
        });
        points = [...gcpPoints.filter((pt) => !pt.imported), ...remade];
      }
    }

    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const oc = off.getContext("2d");
    oc.imageSmoothingEnabled = true;
    oc.imageSmoothingQuality = "high";
    oc.drawImage(img, 0, 0, w, h);
    cropToPolygon({
      off,
      oc,
      realPts: poly.map(([x, y]) => ({ x, y })),
      rotation: 0,
      points,
      message: "Trimmed to the village boundary.",
    });
  };

  const handleResetTraceMask = () => {
    // Points placed on the cut-out go back onto the uncropped image with it;
    // when how it was cut is unknown they can't, and would land in the wrong
    // place, so they are cleared.
    if (cropState) {
      setGcpPoints(pointsToBase(gcpPoints, cropState));
    } else if (gcpPoints.length) {
      setGcpPoints([]);
      toast.warning("Control points were cleared: they can't be moved back onto the uncropped image.");
    }
    setCustomCroppedSrc(null);
    setCropState(null);
    setTracePoints([]);
    setTraceRotation(0);
    setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
    setXform(null);
    refreshOverlay(null);
    overlayDataUrlRef.current = null;
    currentTileUrlRef.current = null;
    setShowTraceModal(false);
    toast.info("Restored original un-cropped sheet image.");
  };

  /**
   * Shows the sheet's original PDF in place of its old browser cut-out. The
   * server finds where the old cut-out sat in the PDF and moves the control
   * points onto it, so they stay on the same features and the sheet lands
   * where it was. Saving then crops the PDF (sharp) instead of the cut-out.
   */
  const handleStartFromOriginal = async () => {
    const id = selectedFileId || fileId;
    setStartingOriginal(true);
    let carried = null;
    let reason = "";
    try {
      const res = await Axios.post(`/maps/map-files/${id}/georeference/sharpen/check`, {}, authHeaders());
      carried = res.data?.data?.carried || null;
      reason = res.data?.data?.reason || "";
    } catch (err) {
      reason = err?.response?.data?.message || "";
    }

    setUseOriginal(true);
    setCustomCroppedSrc(null);
    setCropState(null);
    setTracePoints([]);
    setTraceRotation(0);
    setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
    setXform(null);
    refreshOverlay(null);
    overlayDataUrlRef.current = null;
    currentTileUrlRef.current = null;
    setAlignmentMode("gcp");

    if (carried?.gcp_points?.length >= 3) {
      expectedPreviewSizeRef.current = carried.preview_size || null;
      setGcpPoints(carried.gcp_points);
      toast.success(
        `Original PDF loaded with your ${carried.gcp_points.length} control points moved onto it. ` +
          "Crop it again; the points move with the crop."
      );
    } else {
      expectedPreviewSizeRef.current = null;
      setGcpPoints([]);
      toast.warning(
        "Original PDF loaded, but the old crop couldn't be found in it, so the control points couldn't be " +
          `moved over. Place them again.${reason ? ` (${reason})` : ""}`
      );
    }
    setStartingOriginal(false);
    setShowStartOriginal(false);
  };

  const handleRotateWorkspaceImage = (delta) => {
    if (isLocked) {
      toast.info("Overlay position is locked. Unlock to rotate image.");
      return;
    }
    const img = imageRef.current;
    if (!img) return;

    const natW = img.naturalWidth || 1000;
    const natH = img.naturalHeight || 1000;

    const isRotated90 = Math.abs(delta) === 90 || Math.abs(delta) === 270;
    const effW = isRotated90 ? natH : natW;
    const effH = isRotated90 ? natW : natH;

    const off = document.createElement("canvas");
    off.width = effW;
    off.height = effH;
    const oc = off.getContext("2d");
    oc.imageSmoothingEnabled = true;
    oc.imageSmoothingQuality = "high";

    oc.save();
    if (delta === 90 || delta === -270) {
      oc.translate(effW, 0);
      oc.rotate((90 * Math.PI) / 180);
      oc.drawImage(img, 0, 0, natW, natH);
    } else if (delta === -90 || delta === 270) {
      oc.translate(0, effH);
      oc.rotate((270 * Math.PI) / 180);
      oc.drawImage(img, 0, 0, natW, natH);
    } else if (delta === 180 || delta === -180) {
      oc.translate(effW, effH);
      oc.rotate(Math.PI);
      oc.drawImage(img, 0, 0, natW, natH);
    } else {
      oc.drawImage(img, 0, 0, natW, natH);
    }
    oc.restore();

    const dataUrl = off.toDataURL("image/png");
    const currentRecipe = cropState || (customCroppedSrc ? null : identityRecipe(natW, natH));
    setCropState(currentRecipe ? rotateRecipe(currentRecipe, delta) : null);
    setCustomCroppedSrc(dataUrl);
    overlayDataUrlRef.current = null;
    currentTileUrlRef.current = null;
    setTracePoints([]);
    setGcpPoints(rotatePoints(gcpPoints, natW, natH, delta));
    setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
    setXform(null);
    setVisualCorners([]);
    refreshOverlay(null);
    toast.success(`Image rotated ${delta > 0 ? "+90°" : "-90°"} successfully!`);
  };

  const solveGcp = useCallback(
    (currentGcps, modelType = null, refit = true) => {
      const activeModel = modelType || (currentGcps.length >= 3 ? "aff" : fitModel);
      const need = activeModel === "aff" ? 3 : 2;
      const enough = currentGcps.length >= need;

      if (!enough) {
        setXform(null);
        setVisualCorners([]);
        refreshOverlay(null);
        if (currentGcps.length === 0) {
          initialCenterDoneRef.current = false;
        }
        setRmsStats({
          text: currentGcps.length
            ? `${currentGcps.length}/${need} pairs — need ${need - currentGcps.length} more`
            : "no placement yet — click a point pair",
          status: "badge",
        });
        return;
      }

      let newXform = xform;
      if (refit || !xform || !manualTouched) {
        const pairs = currentGcps.map((g) => ({ x: g.x, y: g.y, X: mX(g.lng), Y: mY(g.lat) }));
        const m = activeModel === "aff" ? fitAffine(pairs) : fitSimilarity(pairs);
        if (m) {
          newXform = m;
          setXform(m);
          setManualTouched(false);
        } else {
          setRmsStats({ text: "fit failed — points are collinear", status: "warn" });
          return;
        }
      }

      if (!newXform) {
        setRmsStats({
          text: `${currentGcps.length}/${need} pairs — need ${need - currentGcps.length} more`,
          status: "badge",
        });
        return;
      }

      // Calculate residuals & RMS Error in meters
      if (currentGcps.length > 0) {
        const latAvg = currentGcps.reduce((s, g) => s + g.lat, 0) / currentGcps.length;
        const k = groundK(latAvg);
        let sse = 0;
        const updatedGcps = currentGcps.map((g) => {
          const q = matApply(newXform, g.x, g.y);
          const resM = Math.hypot(q[0] - mX(g.lng), q[1] - mY(g.lat)) * k;
          sse += resM * resM;
          return { ...g, res: resM, bad: resM > 15 };
        });
        setGcpPoints(updatedGcps);

        const rmsM = Math.sqrt(sse / currentGcps.length);
        const d = decompose(newXform);
        const mpp = d.scale * k;
        const exact = !manualTouched && currentGcps.length === need;

        if (exact) {
          setRmsStats({ text: `exact fit · ${mpp.toFixed(2)} m/px · ${d.rot.toFixed(1)}°`, status: "badge" });
        } else if (manualTouched) {
          setRmsStats({
            text: `hand-adjusted · ${rmsM < 10 ? rmsM.toFixed(1) : Math.round(rmsM)} m from points · ${mpp.toFixed(2)} m/px`,
            status: "badge",
          });
        } else {
          setRmsStats({
            text: `RMS ${rmsM < 10 ? rmsM.toFixed(1) : Math.round(rmsM)} m · ${mpp.toFixed(2)} m/px · ${d.rot.toFixed(1)}°`,
            status: rmsM <= 8 ? "good" : "warn",
          });
        }
      }

      if (newXform && imageRef.current) {
        const w = imageRef.current.naturalWidth || 1000;
        const h = imageRef.current.naturalHeight || 1000;
        const computed = getRotatedCornersFromXform(newXform, w, h);
        if (computed && computed.length === 4) {
          setVisualCorners(computed);
        }
      }

      refreshOverlay(newXform);
    },
    [fitModel, xform, manualTouched, refreshOverlay]
  );
  solveGcpRef.current = solveGcp;

  const handleImageLoad = () => {
    // Points carried onto the original PDF were computed for a preview of a
    // known size; on any other they would be misplaced.
    const expected = expectedPreviewSizeRef.current;
    const img = imageRef.current;
    if (expected && img && !customCroppedSrc) {
      expectedPreviewSizeRef.current = null;
      if (Math.abs(img.naturalWidth - expected[0]) > 1 || Math.abs(img.naturalHeight - expected[1]) > 1) {
        setGcpPoints([]);
        gcpPointsRef.current = [];
        toast.warning("The PDF preview isn't the size the points were moved for, so they were cleared. Place them again.");
        return;
      }
    }
    if (gcpPointsRef.current && gcpPointsRef.current.length >= 3) {
      solveGcp(gcpPointsRef.current, "aff", true);
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;
    try {
      if (map.getLayer("gcp-live-ovl-layer")) {
        map.setPaintProperty("gcp-live-ovl-layer", "raster-opacity", isFlickering ? 0 : Number(gcpOpacity));
      }
    } catch (err) { }
  }, [gcpOpacity, isFlickering, isMapLoaded]);

  // The GCP map is created afresh each time GCP mode opens (and on first
  // load). The sheet image usually finishes loading before the map does, so
  // its overlay can't be drawn then; draw it once the map is ready.
  useEffect(() => {
    if (!isMapLoaded || alignmentMode !== "gcp" || !xform) return;
    currentTileUrlRef.current = null;
    refreshOverlay(xform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded, alignmentMode]);

  // The imported boundary as a dashed outline on the GCP map (on top of the
  // sheet), so the fit can be judged at a glance.
  const boundaryGeometry = mapDetail?.boundary?.geometry || null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded || alignmentMode !== "gcp") return;
    const remove = () => {
      try {
        if (map.getLayer("import-boundary-line")) map.removeLayer("import-boundary-line");
        if (map.getSource("import-boundary")) map.removeSource("import-boundary");
      } catch (e) {}
    };
    remove();
    if (!showBoundary || !boundaryGeometry) return;
    try {
      map.addSource("import-boundary", {
        type: "geojson",
        data: { type: "Feature", properties: {}, geometry: boundaryGeometry },
      });
      map.addLayer({
        id: "import-boundary-line",
        type: "line",
        source: "import-boundary",
        paint: { "line-color": "#facc15", "line-width": 2.5, "line-dasharray": [2, 1.5] },
      });
    } catch (err) {
      console.warn("Could not draw the imported boundary:", err);
    }
  }, [isMapLoaded, alignmentMode, showBoundary, boundaryGeometry]);

  // Frame an imported sheet once its map is ready: the suggested placement,
  // or for a sheet with no position, its boundary.
  useEffect(() => {
    const map = mapRef.current;
    if (!draftFramePendingRef.current || !map || !isMapLoaded || alignmentMode !== "gcp") return;
    let coords = null;
    if (xform && imageRef.current) {
      const w = imageRef.current.naturalWidth || 1000;
      const h = imageRef.current.naturalHeight || 1000;
      coords = [[0, 0], [w, 0], [w, h], [0, h]].map(([x, y]) => {
        const q = matApply(xform, x, y);
        return [iX(q[0]), iY(q[1])];
      });
    } else if (boundaryGeometry && !(mapDetail?.georefDraft?.gcp_points || []).length) {
      const flat = [];
      const walk = (node) => (typeof node[0] === "number" ? flat.push(node) : node.forEach(walk));
      walk(boundaryGeometry.coordinates);
      coords = flat;
    }
    if (!coords || !coords.length) return;
    draftFramePendingRef.current = false;
    const bounds = new maplibregl.LngLatBounds(coords[0], coords[0]);
    coords.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, { padding: 40, duration: 0 });
  }, [isMapLoaded, alignmentMode, xform, boundaryGeometry, mapDetail]);

  const importedPointCount = gcpPoints.filter((pt) => pt.imported).length;
  const ownPointCount = gcpPoints.length - importedPointCount;

  // Once the admin's own points can fix the sheet alone, the imported ones
  // would only pull the fit back towards the suggestion.
  const promptedDropRef = useRef(false);
  useEffect(() => {
    if (importedPointCount > 0 && ownPointCount >= 3 && !promptedDropRef.current) {
      promptedDropRef.current = true;
      toast.info("You have placed 3 points. Drop the imported points so only yours decide the fit.");
    }
    if (ownPointCount < 3) promptedDropRef.current = false;
  }, [importedPointCount, ownPointCount, toast]);

  const handleDropImportedPoints = () => {
    if (ownPointCount < 3) {
      toast.info("Place at least 3 points of your own first; until then the imported ones hold the sheet in place.");
      return;
    }
    const own = gcpPoints.filter((pt) => !pt.imported);
    setGcpPoints(own);
    solveGcp(own, "aff", true);
    toast.success(`Dropped ${importedPointCount} imported ${importedPointCount === 1 ? "point" : "points"}; the fit now uses your ${own.length}.`);
  };

  const handleGoToSheet = () => {
    const map = mapRef.current;
    if (!map || !xform || !imageRef.current) return;

    const w = imageRef.current.naturalWidth || 1000;
    const h = imageRef.current.naturalHeight || 1000;

    const coords = [
      [0, 0],
      [w, 0],
      [w, h],
      [0, h],
    ].map(([x, y]) => {
      const q = matApply(xform, x, y);
      return [iX(q[0]), iY(q[1])];
    });

    const bounds = new maplibregl.LngLatBounds(coords[0], coords[0]);
    coords.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, { padding: 40, duration: 600 });
  };

  // Smart Free Transform Button Handler (Drops sheet on first click & toggles handles)
  const handleToggleFreeTransform = () => {
    const map = mapRef.current;
    if (!map || !imageRef.current) return;

    if (!xform) {
      const b = map.getBounds();
      const w = imageRef.current.naturalWidth || 1000;
      const h = imageRef.current.naturalHeight || 1000;

      const westM = mX(b.getWest());
      const eastM = mX(b.getEast());
      const southM = mY(b.getSouth());
      const northM = mY(b.getNorth());

      const s = Math.min((eastM - westM) / w, (northM - southM) / h) * 0.6;
      const cx = (westM + eastM) / 2;
      const cy = (southM + northM) / 2;

      const newMatrix = { a: s, b: 0, c: 0, d: -s, e: cx - (s * w) / 2, f: cy + (s * h) / 2 };
      setXform(newMatrix);
      setManualTouched(true);
      refreshOverlay(newMatrix);
      setRmsStats({ text: "placed by hand on map", status: "badge" });
      setFreeMode(true);
      return;
    }

    setFreeMode((prev) => !prev);
  };

  // Center Sheet Here Function (Translates overlay center to current map view center)
  const handleCenterHere = useCallback(() => {
    const map = mapRef.current;
    if (!map || !imageRef.current) return;

    const b = map.getBounds();
    const w = imageRef.current.naturalWidth || 1000;
    const h = imageRef.current.naturalHeight || 1000;

    const westM = mX(b.getWest());
    const eastM = mX(b.getEast());
    const southM = mY(b.getSouth());
    const northM = mY(b.getNorth());

    const cx = (westM + eastM) / 2;
    const cy = (southM + northM) / 2;

    if (!xform) {
      const s = Math.min((eastM - westM) / w, (northM - southM) / h) * 0.6;
      const newMatrix = { a: s, b: 0, c: 0, d: -s, e: cx - (s * w) / 2, f: cy + (s * h) / 2 };
      setXform(newMatrix);
      setManualTouched(true);
      refreshOverlay(newMatrix);
      setRmsStats({ text: "placed by hand on map", status: "badge" });
      setFreeMode(true);
    } else {
      const currentCtr = matApply(xform, w / 2, h / 2);
      const dx = cx - currentCtr[0];
      const dy = cy - currentCtr[1];
      const newMatrix = matMul(matTrans(dx, dy), xform);
      setXform(newMatrix);
      setManualTouched(true);
      refreshOverlay(newMatrix);
      setRmsStats({ text: "moved to center of map", status: "badge" });
    }
  }, [xform, refreshOverlay]);

  // ═══ Micro-Nudge Transformation Controls ═══════════════════════════════
  const centreMerc = useCallback((matrix = xformRef.current) => {
    if (!matrix || !imageRef.current) return [0, 0];
    const w = imageRef.current.naturalWidth || 1000;
    const h = imageRef.current.naturalHeight || 1000;
    return matApply(matrix, w / 2, h / 2);
  }, []);

  const afterNudge = useCallback(
    (newMatrix) => {
      setXform(newMatrix);
      setManualTouched(true);
      refreshOverlay(newMatrix);

      if (imageRef.current) {
        const w = imageRef.current.naturalWidth || 1000;
        const h = imageRef.current.naturalHeight || 1000;
        const computed = getRotatedCornersFromXform(newMatrix, w, h);
        if (computed && computed.length === 4) {
          setVisualCorners(computed);
        }
      }

      if (gcpPoints.length > 0) {
        const latAvg = gcpPoints.reduce((s, g) => s + g.lat, 0) / gcpPoints.length;
        const k = groundK(latAvg);
        let sse = 0;
        const updatedGcps = gcpPoints.map((g) => {
          const q = matApply(newMatrix, g.x, g.y);
          // Imported points only stand for the suggested placement, so they
          // move with the sheet: what is saved is where it was put by hand.
          if (g.imported) {
            return { ...g, lng: parseFloat(iX(q[0]).toFixed(8)), lat: parseFloat(iY(q[1]).toFixed(8)), res: 0, bad: false };
          }
          const resM = Math.hypot(q[0] - mX(g.lng), q[1] - mY(g.lat)) * k;
          sse += resM * resM;
          return { ...g, res: resM, bad: resM > 15 };
        });
        setGcpPoints(updatedGcps);
        const rmsM = Math.sqrt(sse / gcpPoints.length);
        const d = decompose(newMatrix);
        const mpp = d.scale * k;
        setRmsStats({
          text: `hand-adjusted · ${rmsM < 10 ? rmsM.toFixed(1) : Math.round(rmsM)} m from points · ${mpp.toFixed(2)} m/px · ${d.rot.toFixed(1)}°`,
          status: "badge",
        });
      } else {
        const d = decompose(newMatrix);
        const c = centreMerc(newMatrix);
        const k = groundK(iY(c[1]));
        setRmsStats({
          text: `placed by hand · ${(d.scale * k).toFixed(3)} m/px · ${d.rot.toFixed(1)}°`,
          status: "badge",
        });
      }
    },
    [gcpPoints, refreshOverlay, centreMerc]
  );

  const nudgeRot = (deg) => {
    const currentXform = xformRef.current;
    if (!currentXform) {
      handleCenterHere();
      return;
    }
    const c = centreMerc(currentXform);
    const newM = matMul(matRotAbout((deg * Math.PI) / 180, c[0], c[1]), currentXform);
    afterNudge(newM);
  };

  const nudgeScale = useCallback(
    (factor) => {
      const currentXform = xformRef.current;
      if (!currentXform) {
        handleCenterHere();
        return;
      }
      const c = centreMerc(currentXform);
      const newM = matMul(matScaleAbout(factor, c[0], c[1]), currentXform);
      afterNudge(newM);
    },
    [afterNudge, centreMerc, handleCenterHere]
  );

  const nudgeRotIntervalRef = useRef(null);

  const startContinuousNudgeRot = (deg) => {
    stopContinuousNudgeRot();
    nudgeRot(deg);
    nudgeRotIntervalRef.current = setInterval(() => {
      nudgeRot(deg);
    }, 40);
  };

  const stopContinuousNudgeRot = () => {
    if (nudgeRotIntervalRef.current) {
      clearInterval(nudgeRotIntervalRef.current);
      nudgeRotIntervalRef.current = null;
    }
  };

  const nudgeScaleIntervalRef = useRef(null);

  const startContinuousNudgeScale = (factor) => {
    stopContinuousNudgeScale();
    const initialFactor = factor > 1 ? 1.05 : 0.95;
    nudgeScale(initialFactor);

    const stepFactor = factor > 1 ? 1.015 : 0.985;
    nudgeScaleIntervalRef.current = setInterval(() => {
      nudgeScale(stepFactor);
    }, 40);
  };

  const stopContinuousNudgeScale = () => {
    if (nudgeScaleIntervalRef.current) {
      clearInterval(nudgeScaleIntervalRef.current);
      nudgeScaleIntervalRef.current = null;
    }
  };

  // ═══ SVG Free Transform Handle Overlay Math & Renderers ═════════════════
  const [, setHandleTick] = useState(0);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const rerender = () => setHandleTick((t) => t + 1);
    map.on("move", rerender);
    map.on("zoom", rerender);
    map.on("resize", rerender);

    return () => {
      map.off("move", rerender);
      map.off("zoom", rerender);
      map.off("resize", rerender);
    };
  }, [isMapLoaded]);

  const getImgAnchors = useCallback(() => {
    if (!imageRef.current) return null;
    const w = imageRef.current.naturalWidth || 1000;
    const h = imageRef.current.naturalHeight || 1000;
    return {
      corners: [
        { id: "c0", x: 0, y: 0, ox: w, oy: h },
        { id: "c1", x: w, y: 0, ox: 0, oy: h },
        { id: "c2", x: w, y: h, ox: 0, oy: 0 },
        { id: "c3", x: 0, y: h, ox: w, oy: 0 },
      ],
      edges: [
        { id: "top", x: w / 2, y: 0, axis: "y", anchor: h, kind: "horiz" },
        { id: "right", x: w, y: h / 2, axis: "x", anchor: 0, kind: "vert" },
        { id: "bottom", x: w / 2, y: h, axis: "y", anchor: 0, kind: "horiz" },
        { id: "left", x: 0, y: h / 2, axis: "x", anchor: w, kind: "vert" },
      ],
      centre: { x: w / 2, y: h / 2 },
    };
  }, []);

  const toScreen = useCallback(
    (x, y) => {
      const map = mapRef.current;
      if (!map || !xform) return [0, 0];
      const q = matApply(xform, x, y);
      const p = map.project([iX(q[0]), iY(q[1])]);
      return [p.x, p.y];
    },
    [xform]
  );

  const screenToMerc = useCallback((px, py) => {
    const map = mapRef.current;
    if (!map) return [0, 0];
    const ll = map.unproject([px, py]);
    return [mX(ll.lng), mY(ll.lat)];
  }, []);

  const handleXformPointerDown = (e) => {
    if (isLocked) return;
    const handleId = e.target.getAttribute("data-h");
    if (!handleId || !mapRef.current || !xform || !mapContainerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    mapRef.current.dragPan.disable();

    const A = getImgAnchors();
    if (!A) return;

    const startX = e.clientX;
    const startY = e.clientY;
    let lastX = startX;
    let lastY = startY;
    const initialMatrix = { ...xform };
    const rect = mapContainerRef.current.getBoundingClientRect();

    const onPointerMove = (moveEvent) => {
      const curScreenX = moveEvent.clientX - rect.left;
      const curScreenY = moveEvent.clientY - rect.top;
      const startScreenX = startX - rect.left;
      const startScreenY = startY - rect.top;

      if (handleId === "body") {
        if (moveEvent.altKey || e.altKey) {
          // Alt + Drag anywhere inside polygon rotates around center
          const ctr = toScreen(A.centre.x, A.centre.y);
          const a0 = Math.atan2(startScreenY - ctr[1], startScreenX - ctr[0]);
          const a1 = Math.atan2(curScreenY - ctr[1], curScreenX - ctr[0]);
          const dRot = a1 - a0;
          const cM = matApply(initialMatrix, A.centre.x, A.centre.y);
          const newM = matMul(matRotAbout(-dRot, cM[0], cM[1]), initialMatrix);
          afterNudge(newM);
        } else if (moveEvent.ctrlKey || e.ctrlKey || moveEvent.metaKey || e.metaKey) {
          // Ctrl / Cmd + Drag -> Pan the BASE MAP camera smoothly
          const dx = moveEvent.clientX - lastX;
          const dy = moveEvent.clientY - lastY;
          lastX = moveEvent.clientX;
          lastY = moveEvent.clientY;
          if (mapRef.current) {
            mapRef.current.panBy([-dx, -dy], { animate: false });
          }
        } else {
          // Normal translation move
          const m0 = screenToMerc(startScreenX, startScreenY);
          const m1 = screenToMerc(curScreenX, curScreenY);
          const dx = m1[0] - m0[0];
          const dy = m1[1] - m0[1];
          const newM = matMul(matTrans(dx, dy), initialMatrix);
          afterNudge(newM);
        }
      } else if (handleId === "rot") {
        const ctr = toScreen(A.centre.x, A.centre.y);
        const a0 = Math.atan2(startScreenY - ctr[1], startScreenX - ctr[0]);
        const a1 = Math.atan2(curScreenY - ctr[1], curScreenX - ctr[0]);
        const dRot = a1 - a0;
        const cM = matApply(initialMatrix, A.centre.x, A.centre.y);
        const newM = matMul(matRotAbout(-dRot, cM[0], cM[1]), initialMatrix);
        afterNudge(newM);
      } else if (handleId.startsWith("c:")) {
        const idx = parseInt(handleId.split(":")[1], 10);
        const c0 = A.corners[idx];
        const ancMerc = matApply(initialMatrix, c0.ox, c0.oy);
        const curMerc = screenToMerc(curScreenX, curScreenY);
        const dx0 = matApply(initialMatrix, c0.x, c0.y)[0] - ancMerc[0];
        const dy0 = matApply(initialMatrix, c0.x, c0.y)[1] - ancMerc[1];
        const dx1 = curMerc[0] - ancMerc[0];
        const dy1 = curMerc[1] - ancMerc[1];
        const len0 = Math.hypot(dx0, dy0) || 1;
        const len1 = Math.hypot(dx1, dy1);
        const k = len1 / len0;
        const newM = matMul(matScaleAbout(k, ancMerc[0], ancMerc[1]), initialMatrix);
        afterNudge(newM);
      } else if (handleId.startsWith("e:")) {
        const edgeId = handleId.split(":")[1];
        const e0 = A.edges.find((ed) => ed.id === edgeId);
        if (!e0) return;
        const inv = matInv(initialMatrix);
        if (!inv) return;
        const curMerc = screenToMerc(curScreenX, curScreenY);
        const imgCur = matApply(inv, curMerc[0], curMerc[1]);
        const den = (e0.axis === "x" ? e0.x : e0.y) - e0.anchor;
        let k = Math.abs(den) < 1e-6 ? 1 : ((e0.axis === "x" ? imgCur[0] : imgCur[1]) - e0.anchor) / den;
        k = Math.sign(k || 1) * Math.max(0.02, Math.abs(k));
        const S = e0.axis === "x" ? { a: k, b: 0, c: 0, d: 1, e: e0.anchor * (1 - k), f: 0 } : { a: 1, b: 0, c: 0, d: k, e: 0, f: e0.anchor * (1 - k) };
        const newM = matMul(initialMatrix, S);
        afterNudge(newM);
      }
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      if (mapRef.current) mapRef.current.dragPan.enable();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const svgNodeRef = useRef(null);
  const svgRef = useCallback((node) => {
    if (svgNodeRef.current) {
      if (svgNodeRef.current._onWheelNative) {
        svgNodeRef.current.removeEventListener("wheel", svgNodeRef.current._onWheelNative);
      }
    }
    svgNodeRef.current = node;
    if (node) {
      const onWheelNative = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const map = mapRef.current;
        if (!map) return;

        // Shift + Wheel resizes overlay image scale on map when unlocked
        if (e.shiftKey && !isLockedRef.current) {
          const scaleFactor = e.deltaY < 0 ? 1.05 : 0.95;
          nudgeScale(scaleFactor);
          return;
        }

        // Normal Wheel zooms base map camera
        if (e.deltaY < 0) {
          map.zoomIn({ duration: 150 });
        } else if (e.deltaY > 0) {
          map.zoomOut({ duration: 150 });
        }
      };
      node.addEventListener("wheel", onWheelNative, { passive: false });
      node._onWheelNative = onWheelNative;
    }
  }, [nudgeScale]);

  const renderSvgTransformHandles = () => {
    if (!freeMode || !xform || !imageRef.current || !mapRef.current || isLocked) return null;
    const A = getImgAnchors();
    if (!A) return null;

    const cs = A.corners.map((c) => toScreen(c.x, c.y));
    const topMid = toScreen(A.edges[0].x, A.edges[0].y);
    const ctr = toScreen(A.centre.x, A.centre.y);

    let nx = topMid[0] - ctr[0];
    let ny = topMid[1] - ctr[1];
    const nl = Math.hypot(nx, ny) || 1;
    const knob = [topMid[0] + (nx / nl) * 34, topMid[1] + (ny / nl) * 34];

    return (
      <svg
        ref={svgRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 20,
        }}
        onPointerDown={handleXformPointerDown}
      >
        {/* Draggable Sheet Body */}
        <polygon
          points={cs.map((p) => p.join(",")).join(" ")}
          style={{
            fill: "rgba(99, 102, 241, 0.12)",
            stroke: "rgba(99, 102, 241, 0.8)",
            strokeWidth: 1.5,
            strokeDasharray: "4,4",
            cursor: "grab",
            pointerEvents: "all",
          }}
          data-h="body"
        />

        {/* Orange Rotation Knob */}
        <line x1={topMid[0]} y1={topMid[1]} x2={knob[0]} y2={knob[1]} style={{ stroke: "#f97316", strokeWidth: 2 }} />
        <circle
          cx={knob[0]}
          cy={knob[1]}
          r={8}
          style={{ fill: "#f97316", stroke: "#ffffff", strokeWidth: 2, cursor: "crosshair", pointerEvents: "all" }}
          data-h="rot"
        />

        {/* 4 Edge Handles */}
        {A.edges.map((e) => {
          const p = toScreen(e.x, e.y);
          return (
            <rect
              key={e.id}
              x={p[0] - 5}
              y={p[1] - 5}
              width={10}
              height={10}
              rx={2}
              style={{ fill: "#ffffff", stroke: "#6366f1", strokeWidth: 2, cursor: e.kind === "horiz" ? "ns-resize" : "ew-resize", pointerEvents: "all" }}
              data-h={`e:${e.id}`}
            />
          );
        })}

        {/* 4 Corner Handles */}
        {cs.map((p, i) => (
          <rect
            key={i}
            x={p[0] - 6}
            y={p[1] - 6}
            width={12}
            height={12}
            rx={2}
            style={{ fill: "#6366f1", stroke: "#ffffff", strokeWidth: 2, cursor: "nwse-resize", pointerEvents: "all" }}
            data-h={`c:${i}`}
          />
        ))}
      </svg>
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setCustomCroppedSrc(null);
      setCropState(null);
      setUseOriginal(false);
      setGcpPoints([]); // Reset points for new dimensions
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
      setImageZoom(1);
      setPanOffset({ x: 0, y: 0 });
      toast.info("Uploaded a new bordered image. GCP points have been reset.");
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setUploadedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setCustomCroppedSrc(null);
      setCropState(null);
      setUseOriginal(false);
      setGcpPoints([]); // Reset points
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
      setImageZoom(1);
      setPanOffset({ x: 0, y: 0 });
      toast.info("Imported new bordered image via drag-and-drop. GCP points reset.");
    } else {
      toast.error("Please drop a valid image file (PNG/JPG).");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const saveGcpPair = () => {
    if (activeTabPoint.x === null || activeTabPoint.lng === null) {
      toast.error("Please specify both Image coordinates (click image) and Map coordinates (click map).");
      return;
    }
    const updatedList = [...gcpPoints, activeTabPoint];
    setGcpPoints(updatedList);
    setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
    solveGcp(updatedList, fitModel, true);
  };

  const removeGcpPair = (idx) => {
    if (isLocked) {
      toast.info("Overlay position is locked. Unlock to delete control points.");
      return;
    }
    const updatedList = gcpPoints.filter((_, i) => i !== idx);
    setGcpPoints(updatedList);
    if (updatedList.length === 0) {
      setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
    }
    solveGcp(updatedList, fitModel, true);
  };

  const handleSubmit = async () => {
    if (alignmentMode === "gcp" && gcpPoints.length < 3) {
      toast.error("At least 3 Ground Control Points are required to warp/georeference the sheet.");
      return;
    }

    if (alignmentMode === "visual" && (!visualCorners || visualCorners.length !== 4)) {
      toast.error("Please drag and place the map sheet on the baseline map.");
      return;
    }

    // An imported village shown whole would put its legend, title block and
    // neighbours' land on the map.
    if (
      mapDetail?.georefDraft &&
      !mapDetail?.georeference &&
      mapDetail?.boundary?.geometry &&
      !customCroppedSrc &&
      !window.confirm(
        "This village isn't trimmed to its boundary, so its legend, title block and the land around it will show on the map.\n\nSave it untrimmed anyway? (Cancel, then use Trim to boundary.)"
      )
    ) {
      return;
    }

    const hasImage = customCroppedSrc || uploadedFile || mapDetail?.georeference?.georef_original_url || mapDetail?.file_url;
    if (!hasImage) {
      toast.error("No source map image found to perform georeferencing.");
      return;
    }

    // Preferably the crop as a recipe: the backend rebuilds it from the
    // original PDF at full resolution. Otherwise (a new upload, a sheet saved
    // the old way, or a crop whose history is unknown) the cut-out itself.
    let fileToUpload = uploadedFile;
    let cropToSend = null;
    if (customCroppedSrc && recipeEligible && cropState) {
      cropToSend = cropState;
    } else if (useOriginal && !customCroppedSrc) {
      // Started again from the PDF and kept the whole page: still a recipe,
      // so the server uses the PDF rather than the old cut-out.
      const img = imageRef.current;
      cropToSend = identityRecipe(img?.naturalWidth || 1000, img?.naturalHeight || 1000);
    } else if (customCroppedSrc) {
      fileToUpload = dataURLtoFile(customCroppedSrc, `cropped_${selectedFileId || fileId}.png`);
    }

    let computedCorners = visualCorners;
    if (alignmentMode === "gcp" && xform) {
      const w = imageRef.current?.naturalWidth || 1000;
      const h = imageRef.current?.naturalHeight || 1000;
      computedCorners = getRotatedCornersFromXform(xform, w, h);
    }

    setLoading(true);
    try {
      await dispatch(
        georeferenceMapFile({
          id: selectedFileId || fileId,
          gcpPoints: alignmentMode === "gcp" ? gcpPoints : [],
          corners: computedCorners,
          alignmentMode,
          file: fileToUpload,
          crop: cropToSend,
        })
      ).unwrap();

      toast.success("Map successfully placed and georeferenced!");
      navigate("/maps-georeference");
    } catch (err) {
      console.error("Georeferencing dispatch failure:", err);
      toast.error(err?.message || "Failed to process georeferencing on backend.");
    } finally {
      setLoading(false);
    }
  };

  const handleCornersChange = React.useCallback((c) => {
    setVisualCorners(c);
  }, []);


  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Left click only
    setIsPanning(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    startOffset.current = { ...panOffset };

    const onWindowMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - dragStart.current.x;
      const dy = moveEvent.clientY - dragStart.current.y;
      setPanOffset({
        x: startOffset.current.x + dx,
        y: startOffset.current.y + dy,
      });
    };

    const onWindowMouseUp = (upEvent) => {
      setIsPanning(false);
      window.removeEventListener("mousemove", onWindowMouseMove);
      window.removeEventListener("mouseup", onWindowMouseUp);

      const dx = upEvent.clientX - dragStart.current.x;
      const dy = upEvent.clientY - dragStart.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If the cursor barely moved, treat it as a click to place a GCP point
      if (distance < 5) {
        handleImageClick(upEvent);
      }
    };

    window.addEventListener("mousemove", onWindowMouseMove);
    window.addEventListener("mouseup", onWindowMouseUp);
  };

  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 w-100 pe-3">
      <div className="d-flex align-items-center gap-3">
        <Button
          variant="outline-secondary"
          size="sm"
          className="border-0 p-1"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h5 className="mb-0 fw-semibold text-primary fs-6">
            Georeference / Warp Map Sheet: <span className="text-muted fw-normal">{mapDetail?.title || mapDetail?.file_name || "Select Sheet"}</span>
          </h5>
        </div>

        {/* Map Sheet Selector Dropdown */}
        <div className="ms-2">
          <Form.Select
            size="sm"
            style={{ maxWidth: "260px" }}
            value={selectedFileId}
            onChange={(e) => handleSelectMapSheet(e.target.value)}
          >
            <option value="">-- Select Map Sheet --</option>
            {availableMapFiles.map((file) => (
              <option key={file.id} value={file.id}>
                {file.title || file.file_name} ({file.mapType?.code?.toUpperCase() || "MAP"}) {file.georeference ? "✓" : file.georefDraft ? "• to check" : ""}
              </option>
            ))}
          </Form.Select>
        </div>

        {/* Alignment Mode Switcher */}
        <div className="d-flex align-items-center bg-light p-1 border rounded-3 ms-2">
          <Button
            size="sm"
            variant={alignmentMode === "visual" ? "primary" : "light"}
            className="py-1 px-2.5 fs-xs d-flex align-items-center gap-1.5"
            onClick={() => {
              if (xform && imageRef.current) {
                const w = imageRef.current.naturalWidth || 1000;
                const h = imageRef.current.naturalHeight || 1000;
                const computed = getRotatedCornersFromXform(xform, w, h);
                if (computed && computed.length === 4) {
                  setVisualCorners(computed);
                }
              }
              setAlignmentMode("visual");
            }}
          >
            <Move size={13} />
            Visual Drag & Place
          </Button>
          <Button
            size="sm"
            variant={alignmentMode === "gcp" ? "primary" : "light"}
            className="py-1 px-2.5 fs-xs d-flex align-items-center gap-1.5"
            onClick={() => setAlignmentMode("gcp")}
          >
            <Target size={13} />
            GCP Points
          </Button>
        </div>
      </div>

      <div className="d-flex gap-2">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={
            !selectedFileId ||
            !mapDetail ||
            (!uploadedFile && !mapDetail.georeference?.georef_original_url && !mapDetail.file_url) ||
            (alignmentMode === "gcp"
              ? gcpPoints.length < 3
              : !visualCorners || visualCorners.length !== 4)
          }
          onClick={handleSubmit}
        >
          <CheckCircle size={14} className="me-1" />
          Save & Warp Overlay
        </Button>
      </div>
    </div>
  );

  const draft = mapDetail && !mapDetail.georeference ? mapDetail.georefDraft : null;
  const draftMeta = draft?.import_meta || {};
  const isImportedSheet = Boolean(
    mapDetail && (mapDetail.georefDraft || mapDetail.boundary || mapDetail.georeference?.source === "import")
  );

  // Mirrors what the server changed, without reloading the sheet (which
  // would throw away points being placed).
  const handleVillageSaved = (result, sent) => {
    if (result.title) {
      setAvailableMapFiles((files) => files.map((f) => (String(f.id) === String(result.id) ? { ...f, title: result.title } : f)));
    }
    setMapDetail((prev) => {
      if (!prev) return prev;
      const settle = (meta) => {
        if (!meta) return meta;
        let reasons = meta.review_reasons || [];
        const next = { ...meta };
        if ("census_code" in sent) {
          next.census_code = result.census_code ?? null;
          next.census_code_conflict = null;
          reasons = reasons.filter((r) => !/^Census code /.test(r));
        }
        if ("title" in sent) reasons = reasons.filter((r) => !/^Census name /.test(r));
        return { ...next, review_reasons: reasons };
      };
      return {
        ...prev,
        title: result.title ?? prev.title,
        boundary: prev.boundary
          ? { ...prev.boundary, ...("census_code" in sent ? { census_code: result.census_code ?? null } : {}) }
          : prev.boundary,
        georefDraft: prev.georefDraft ? { ...prev.georefDraft, import_meta: settle(prev.georefDraft.import_meta) } : prev.georefDraft,
        georeference: prev.georeference?.import_meta
          ? { ...prev.georeference, import_meta: settle(prev.georeference.import_meta) }
          : prev.georeference,
      };
    });
  };

  return (
    <Container fluid className="px-3 py-3 h-100 flex-column d-flex bg-light">
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}

      {isImportedSheet && <VillageDetails key={mapDetail.id} detail={mapDetail} onSaved={handleVillageSaved} />}

      {draft && (
        <Alert variant={draft.mode === "image_only" ? "secondary" : "info"} className="py-2 px-3 mb-2 fs-xs">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
            <div style={{ maxWidth: 900 }}>
              <div className="fw-semibold mb-1">
                {draft.mode === "image_only"
                  ? "Imported without a position: not on the map yet."
                  : "Imported draft: not on the map yet. The sheet is where the package suggested."}
                {draftMeta.confidence && (
                  <span className="fw-normal text-muted">
                    {" "}
                    Confidence {draftMeta.confidence}
                    {typeof draftMeta.fit_score === "number" ? ` · fit ${draftMeta.fit_score.toFixed(2)}` : ""}
                    {draftMeta.census_code_conflict ? ` · census code ${draftMeta.census_code_conflict} is shared` : ""}
                  </span>
                )}
              </div>
              {(draftMeta.review_reasons || []).length > 0 && (
                <ul className="mb-1 ps-3">
                  {draftMeta.review_reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
              <div className="text-muted">
                {draft.mode === "image_only"
                  ? "Place it with control points or Visual Drag & Place, using the satellite view, the boundary and neighbouring sheets. Save & Warp puts it on the map."
                  : "Check it against the satellite view, the dashed boundary and neighbouring sheets. Move it with Free Transform, or add points of your own and drop the imported ones. Then Trim to boundary (removes the legend and the land around the village) and Save & Warp to put it on the map."}
              </div>
            </div>
            <div className="d-flex flex-column align-items-end gap-1">
            {boundaryGeometry && (
              <Button
                size="sm"
                variant={customCroppedSrc ? "outline-success" : "primary"}
                className="fs-xs"
                onClick={handleTrimToBoundary}
                disabled={Boolean(customCroppedSrc) || !xform}
                title={
                  customCroppedSrc
                    ? "Already cropped; Reset the crop to trim again"
                    : xform
                      ? "Cut the sheet to the dashed village boundary at its current position"
                      : "Place the sheet first"
                }
              >
                {customCroppedSrc ? "Trimmed ✓" : "Trim to boundary"}
              </Button>
            )}
            {importedPointCount > 0 && (
              <Button
                size="sm"
                variant="outline-primary"
                className="fs-xs"
                onClick={handleDropImportedPoints}
                title={
                  ownPointCount >= 3
                    ? "Fit the sheet to your own points only"
                    : "Place at least 3 points of your own first"
                }
              >
                Drop imported points ({importedPointCount})
                {ownPointCount < 3 && <span className="text-muted"> · you have {ownPointCount}/3</span>}
              </Button>
            )}
            </div>
          </div>
        </Alert>
      )}

      {!selectedFileId || !mapDetail ? (
        <Card className="flex-grow-1 border-0 shadow-sm d-flex align-items-center justify-content-center p-5 text-center">
          <Card.Body style={{ maxWidth: "480px" }}>
            <MapIcon size={48} className="text-primary mb-3" />
            <h5 className="fw-semibold text-dark mb-2">Select a Map Sheet to Georeference</h5>
            <p className="text-muted fs-xs mb-4">
              Choose a map sheet from your registered map files to start interactive Drag & Place alignment or Ground Control Point warping.
            </p>
            <Form.Group className="mb-3 text-start">
              <Form.Label className="fs-xs fw-semibold text-muted">Map Sheet File</Form.Label>
              <Form.Select
                size="sm"
                value={selectedFileId}
                onChange={(e) => handleSelectMapSheet(e.target.value)}
              >
                <option value="">-- Select Map Sheet --</option>
                {availableMapFiles.map((file) => (
                  <option key={file.id} value={file.id}>
                    {file.title || file.file_name} ({file.mapType?.code?.toUpperCase() || "MAP"}) {file.georeference ? "✓ Georeferenced" : file.georefDraft ? "• To check" : "• Pending"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Card.Body>
        </Card>
      ) : alignmentMode === "visual" ? (
        <Card className="border-0 shadow-sm overflow-hidden flex-grow-1" style={{ minHeight: "calc(100vh - 135px)", height: "calc(100vh - 135px)" }}>
          <VisualMapOverlayEditor
            imageUrl={displayImageSrc}
            initialCorners={visualCorners}
            onCornersChange={handleCornersChange}
            onFileUpload={handleFileChange}
            activeFileId={selectedFileId}
          />
        </Card>
      ) : (
        <>
          {/* GCP Toolbar Control Bar */}
          <div className="bg-white p-2 rounded-3 shadow-sm mb-2.5 d-flex align-items-center justify-content-between flex-wrap gap-2 fs-xs border">
            {/* Group 1: Placement & Fitting Model */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant={freeMode ? "primary" : "danger"}
                className="py-1 px-3 fw-semibold me-1"
                onClick={handleToggleFreeTransform}
                disabled={isLocked}
                title={isLocked ? "Unlock overlay position to enable free transform" : "Place sheet on current map view and toggle interactive handles"}
              >
                {freeMode ? "Free Transform ON ✓" : "Free Transform"}
              </Button>
              <Button
                size="sm"
                variant={isLocked ? "warning" : "outline-secondary"}
                className="py-1 px-2.5 fw-semibold d-flex align-items-center gap-1 me-1"
                onClick={() => setIsLocked(!isLocked)}
                disabled={gcpPoints.length < 3}
                title={gcpPoints.length < 3 ? "Tag at least 3 GCP points to enable lock" : isLocked ? "Unlock sheet position" : "Lock sheet position in place"}
                style={{ fontSize: "11px" }}
              >
                {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                {isLocked ? "Locked" : "Lock"}
              </Button>
              <Button
                size="sm"
                variant="link"
                className="text-secondary p-0 ms-1 fs-xs text-decoration-none"
                onClick={() => {
                  if (isLocked) return;
                  setGcpPoints([]);
                  setActiveTabPoint({ x: null, y: null, lng: null, lat: null });
                  setXform(null);
                  setVisualCorners([]);
                  setFreeMode(false);
                  refreshOverlay(null);
                  initialCenterDoneRef.current = false;
                  setRmsStats({ text: "no placement yet — click a point pair", status: "badge" });
                }}
                disabled={!gcpPoints.length || isLocked}
              >
                Clear pts
              </Button>
            </div>

            <div className="border-end mx-1 d-none d-lg-block" style={{ height: "22px" }} />

            {/* Group 2: Micro-Nudge Adjustments */}
            <div className="d-flex align-items-center gap-1 flex-wrap">
              <span className="text-muted fw-semibold me-1" style={{ fontSize: "11px" }}>Nudge:</span>
              <Button size="sm" variant="outline-secondary" className="px-1.5 py-0.5" onMouseDown={() => startContinuousNudgeRot(+0.1)} onMouseUp={stopContinuousNudgeRot} onMouseLeave={stopContinuousNudgeRot} title="Rotate 0.1° anticlockwise (Hold to glide)" disabled={!xform || isLocked}>
                &#8634; 0.1°
              </Button>
              <Button size="sm" variant="outline-secondary" className="px-1.5 py-0.5" onMouseDown={() => startContinuousNudgeRot(-0.1)} onMouseUp={stopContinuousNudgeRot} onMouseLeave={stopContinuousNudgeRot} title="Rotate 0.1° clockwise (Hold to glide)" disabled={!xform || isLocked}>
                &#8635; 0.1°
              </Button>
              <Button size="sm" variant="outline-secondary" className="px-1.5 py-0.5" onClick={() => nudgeRot(90)} title="Rotate 90 degrees" disabled={!xform || isLocked}>
                90°
              </Button>
              <Button size="sm" variant="outline-secondary" className="px-1.5 py-0.5" onMouseDown={() => startContinuousNudgeScale(0.999)} onMouseUp={stopContinuousNudgeScale} onMouseLeave={stopContinuousNudgeScale} title="Shrink 0.1% (Hold to glide)" disabled={!xform || isLocked}>
                -0.1%
              </Button>
              <Button size="sm" variant="outline-secondary" className="px-1.5 py-0.5" onMouseDown={() => startContinuousNudgeScale(1.001)} onMouseUp={stopContinuousNudgeScale} onMouseLeave={stopContinuousNudgeScale} title="Grow 0.1% (Hold to glide)" disabled={!xform || isLocked}>
                +0.1%
              </Button>
            </div>

            <div className="border-end mx-1 d-none d-lg-block" style={{ height: "22px" }} />

            {/* Group 3: Opacity & Inspection */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="d-flex align-items-center gap-1.5">
                <span className="text-muted fw-semibold" style={{ fontSize: "11px" }}>Opacity:</span>
                <Form.Range
                  min={0.1}
                  max={1.0}
                  step={0.05}
                  value={gcpOpacity}
                  onChange={(e) => setGcpOpacity(parseFloat(e.target.value))}
                  style={{ width: "90px", cursor: "pointer" }}
                />
                <span className="badge bg-secondary" style={{ fontSize: "10px" }}>
                  {Math.round(gcpOpacity * 100)}%
                </span>
              </div>

              <Button
                size="sm"
                variant={isFlickering ? "warning" : "outline-secondary"}
                className="py-1 px-2"
                onMouseDown={() => setIsFlickering(true)}
                onMouseUp={() => setIsFlickering(false)}
                onMouseLeave={() => setIsFlickering(false)}
                title="Hold button (or press F key) to temporarily hide overlay for visual inspection"
                style={{ fontSize: "11px" }}
              >
                Flicker (Hold F)
              </Button>

              <Button
                size="sm"
                variant="outline-primary"
                className="py-1 px-2"
                onClick={handleGoToSheet}
                title="Zoom map view to sheet overlay bounds"
                disabled={!xform}
                style={{ fontSize: "11px" }}
              >
                Go to sheet
              </Button>

              {freeMode && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="py-1 px-2 d-flex align-items-center gap-1"
                  onClick={handleCenterHere}
                  disabled={isLocked}
                  title={isLocked ? "Unlock overlay position to center here" : "Move sheet overlay center to current map view"}
                  style={{ fontSize: "11px" }}
                >
                  <Target size={12} />
                  Center here
                </Button>
              )}
            </div>

            {/* Group 4: RMS Error Badge */}
            <div className="d-flex align-items-center">
              <Badge
                bg={rmsStats.status === "good" ? "success" : rmsStats.status === "warn" ? "danger" : "secondary"}
                className="px-2.5 py-1.5 fw-semibold"
                style={{ fontSize: "11px", whiteSpace: "nowrap" }}
              >
                {rmsStats.text}
              </Badge>
            </div>
          </div>

          <Row className="flex-grow-1" style={{ minHeight: "520px" }}>
            {/* Left Side: Clean Bordered Image GCP Tagging / Drag & Drop */}
            <Col md={6} className="pe-md-2 mb-3 mb-md-0 d-flex flex-column">
              <Card className="h-100 border-0 shadow-sm overflow-hidden flex-column d-flex">
                <Card.Header className="bg-white border-0 py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <span className="fw-semibold text-dark fs-xs">
                    Source Bordered Image (1. Click to place Point)
                  </span>
                  {displayImageSrc && (
                    <div className="d-flex align-items-center gap-2">
                      {/* Image Scale Controls */}
                      <div className="d-flex align-items-center bg-light border rounded-2 p-0.5" title="Shift + Mouse Wheel or Hold buttons to micro scale image">
                        <Button
                          size="sm"
                          variant="light"
                          className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
                          title="Zoom Image Out (Hold to glide)"
                          onMouseDown={() => startContinuousZoom(0.999)}
                          onMouseUp={stopContinuousZoom}
                          onMouseLeave={stopContinuousZoom}
                        >
                          <ZoomOut size={13} />
                        </Button>
                        <Badge
                          bg={Math.abs(imageZoom - 1.0) > 0.001 ? "primary" : "secondary"}
                          className="fs-xs px-2"
                          style={{ cursor: "pointer" }}
                          title="Click to reset Image Scale to 100%"
                          onClick={handleResetImageZoom}
                        >
                          Image {(imageZoom * 100).toFixed(1)}%
                        </Badge>
                        <Button
                          size="sm"
                          variant="light"
                          className="py-0.5 px-1.5 fs-xs d-flex align-items-center text-dark"
                          title="Zoom Image In (Hold to glide)"
                          onMouseDown={() => startContinuousZoom(1.001)}
                          onMouseUp={stopContinuousZoom}
                          onMouseLeave={stopContinuousZoom}
                        >
                          <ZoomIn size={13} />
                        </Button>
                      </div>

                      {/* Rotate Source Image Controls */}
                      <div className="d-flex align-items-center bg-white border rounded px-1 py-0.5 shadow-sm">
                        <Button
                          size="sm"
                          variant="light"
                          className="py-0.5 px-1.5 fs-xs d-flex align-items-center gap-1 text-dark"
                          onClick={() => handleRotateWorkspaceImage(-90)}
                          disabled={isLocked}
                          title="Rotate Image 90° Counter-Clockwise"
                        >
                          <RotateCcw size={12} />
                          -90°
                        </Button>
                        <span className="text-muted border-end mx-0.5" style={{ height: "14px" }} />
                        <Button
                          size="sm"
                          variant="light"
                          className="py-0.5 px-1.5 fs-xs d-flex align-items-center gap-1 text-dark"
                          onClick={() => handleRotateWorkspaceImage(+90)}
                          disabled={isLocked}
                          title="Rotate Image 90° Clockwise"
                        >
                          +90°
                          <RotateCw size={12} />
                        </Button>
                      </div>

                      <Button
                        size="sm"
                        variant={customCroppedSrc ? "warning" : "outline-secondary"}
                        className="py-1 px-2.5 fs-xs d-flex align-items-center gap-1.5"
                        onClick={() => setShowTraceModal(true)}
                        disabled={isLocked}
                        title={isLocked ? "Unlock overlay position to crop boundary" : "Draw boundary polygon to crop white margins"}
                      >
                        <Scissors size={13} />
                        {customCroppedSrc ? "Cropped ✓" : "Crop Boundary"}
                      </Button>
                      {customCroppedSrc && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="py-1 px-2 fs-xs d-flex align-items-center gap-1"
                          onClick={handleResetTraceMask}
                          disabled={isLocked}
                          title="Reset to original uploaded unrotated image"
                        >
                          Reset
                        </Button>
                      )}
                      {canStartFromOriginal && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="py-1 px-2.5 fs-xs d-flex align-items-center gap-1.5"
                          onClick={() => setShowStartOriginal(true)}
                          disabled={isLocked}
                          title="This sheet was saved from a low-resolution cut-out. Load its original PDF to re-crop it and save it sharp."
                        >
                          <FileText size={13} />
                          Start again from PDF
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="py-1 px-2.5 fs-xs d-flex align-items-center gap-1.5"
                        onClick={() => {
                          if (isLocked) return;
                          document.getElementById("bordered-image-input").click();
                        }}
                        disabled={isLocked}
                        title={isLocked ? "Unlock overlay position to replace image file" : "Replace source bordered image file"}
                      >
                        <Upload size={13} />
                        Replace Image
                      </Button>
                      <input
                        id="bordered-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="d-none"
                      />
                    </div>
                  )}
                </Card.Header>
                <Card.Body
                  ref={imageContainerRef}
                  className="bg-dark p-0 flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden position-relative"
                >
                  {displayImageSrc ? (
                    <div
                      className="position-relative d-inline-block"
                      style={{
                        cursor: isPanning ? "grabbing" : "crosshair",
                        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${imageZoom})`,
                        transformOrigin: "center center",
                        transition: isPanning ? "none" : "transform 0.15s cubic-bezier(0.2, 0, 0, 1)",
                        userSelect: "none"
                      }}
                      onMouseDown={handleMouseDown}
                      onClick={handleImageClick}
                      onPointerMove={handleImagePointerMove}
                      onPointerLeave={handleImagePointerLeave}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      <img
                        ref={imageRef}
                        src={displayImageSrc}
                        alt="Source sheet representation"
                        className="img-fluid select-none"
                        draggable={false}
                        style={{ maxHeight: "70vh" }}
                        onLoad={handleImageLoad}
                        crossOrigin="anonymous"
                      />

                      {/* Confirmed GCP Pins overlaid absolutely */}
                      {gcpPoints.map((pt, index) => {
                        const natWidth = imageRef.current?.naturalWidth || 1;
                        const natHeight = imageRef.current?.naturalHeight || 1;
                        const pctX = (pt.x / natWidth) * 100;
                        const pctY = (pt.y / natHeight) * 100;
                        return (
                          <div
                            key={index}
                            className="position-absolute translate-middle d-flex align-items-center justify-content-center font-bold text-white shadow-sm"
                            style={{
                              left: `${pctX}%`,
                              top: `${pctY}%`,
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              backgroundColor: pt.bad ? "#ef4444" : "#6366f1",
                              border: "2px solid #fff",
                              fontSize: "9px",
                              zIndex: 10,
                            }}
                          >
                            {index + 1}
                          </div>
                        );
                      })}

                      {/* Temporary Selected Position Pin */}
                      {activeTabPoint.x !== null && (
                        <div
                          className="position-absolute translate-middle d-flex align-items-center justify-content-center font-bold text-white"
                          style={{
                            left: `${(activeTabPoint.x / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                            top: `${(activeTabPoint.y / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            backgroundColor: "#ef4444",
                            border: "2px solid #fff",
                            fontSize: "9px",
                            zIndex: 11,
                            animation: "pulse 1.5s infinite",
                          }}
                        >
                          ?
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center p-4 border border-dashed transition-all ${isDragging ? "border-primary bg-secondary text-primary" : "border-secondary text-muted"
                        }`}
                      style={{ minHeight: "350px", cursor: "pointer", backgroundColor: "#1e293b" }}
                      onClick={() => document.getElementById("bordered-image-input-empty").click()}
                    >
                      <Upload size={48} className="mb-3 text-secondary" />
                      <p className="fw-semibold mb-1 text-white">Drag and drop your clean bordered image here</p>
                      <p className="text-muted fs-xs mb-3">Supports PNG, JPG, JPEG (Max 15MB)</p>
                      <Button size="sm" variant="primary">
                        Browse Files
                      </Button>
                      <input
                        id="bordered-image-input-empty"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="d-none"
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Right Side: Map Reference Tagging */}
            <Col md={6} className="ps-md-2 d-flex flex-column">
              <Card className="h-100 border-0 shadow-sm overflow-hidden flex-column d-flex">
                <Card.Header className="bg-white border-0 py-2 px-3 d-flex align-items-center justify-content-start flex-wrap gap-2" style={{ minHeight: "44px" }}>
                  <div className="d-flex align-items-center gap-1.5 flex-wrap w-100 justify-content-start">
                    {/* Location Search Bar */}
                    <Form onSubmit={handleGcpLocationSearch} className="d-flex align-items-center me-1">
                      <div className="position-relative d-flex align-items-center">
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Search place or lat, lng..."
                          value={gcpSearchQuery}
                          onChange={(e) => setGcpSearchQuery(e.target.value)}
                          className="pe-4 fs-xs"
                          style={{ width: "175px", height: "26px", fontSize: "11px" }}
                        />
                        {gcpSearchQuery ? (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => setGcpSearchQuery("")}
                            className="position-absolute end-0 p-1 text-secondary border-0"
                          >
                            <X size={12} />
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            variant="link"
                            size="sm"
                            disabled={isGcpSearching}
                            className="position-absolute end-0 p-1 text-secondary border-0"
                          >
                            {isGcpSearching ? (
                              <div className="spinner-border spinner-border-sm" role="status" style={{ width: "11px", height: "11px" }} />
                            ) : (
                              <Search size={12} />
                            )}
                          </Button>
                        )}
                      </div>
                    </Form>
                    {/* Basemap Style Switcher */}
                    <div className="d-flex align-items-center bg-light border rounded-2 p-0.5 me-1" style={{ transform: "scale(0.9)", transformOrigin: "right center" }}>
                      <Button
                        size="sm"
                        variant={mapStyle === "streets" ? "primary" : "light"}
                        className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1"
                        onClick={() => setMapStyle("streets")}
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                      >
                        <Layers size={12} />
                        Streets
                      </Button>
                      <Button
                        size="sm"
                        variant={mapStyle === "satellite" ? "primary" : "light"}
                        className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1"
                        onClick={() => setMapStyle("satellite")}
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                      >
                        <Globe size={12} />
                        Satellite
                      </Button>
                    </div>

                    {/* Nearby Completed Sheets Toggle */}
                    <Button
                      size="sm"
                      variant={showGcpNearbyCompleted ? "primary" : "outline-secondary"}
                      className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1 me-1"
                      onClick={() => setShowGcpNearbyCompleted(!showGcpNearbyCompleted)}
                      title="Toggle visibility of nearby completed map sheets"
                      style={{ fontSize: "11px", padding: "2px 8px" }}
                    >
                      <MapIcon size={12} />
                      {showGcpNearbyCompleted ? "Hide Nearby" : "Show Nearby"}
                    </Button>

                    {/* Imported boundary toggle */}
                    {boundaryGeometry && (
                      <Button
                        size="sm"
                        variant={showBoundary ? "primary" : "outline-secondary"}
                        className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1 me-1"
                        onClick={() => setShowBoundary((v) => !v)}
                        title="The village boundary that came with the import (dashed yellow)"
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                      >
                        {showBoundary ? "Hide Boundary" : "Show Boundary"}
                      </Button>
                    )}

                    {/* Map Type Filter */}
                    {showGcpNearbyCompleted && (
                      <div style={{ minWidth: "155px" }} className="me-1">
                        <Select
                          placeholder="All Map Types"
                          options={gcpMapTypes.map((t) => ({ value: t.id, label: t.name }))}
                          value={gcpSelectedMapTypes}
                          isMulti={true}
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          components={{ Option: CheckboxOption, MultiValue: MultiValueMessage }}
                          styles={mapTypeSelectStyles}
                          onChange={(option) => setGcpSelectedMapTypes(option || [])}
                          isClearable={true}
                          className="mb-0"
                        />
                      </div>
                    )}

                    {freeMode && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="py-0.5 px-2 fs-xs d-flex align-items-center gap-1"
                        onClick={handleCenterHere}
                        disabled={isLocked}
                        title={isLocked ? "Unlock overlay position to center here" : "Move sheet overlay center to current map view"}
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                      >
                        <Target size={12} />
                        Center here
                      </Button>
                    )}
                    {activeTabPoint.lng !== null && (
                      <span className="badge bg-success">
                        Selected Map: {activeTabPoint.lng}, {activeTabPoint.lat}
                      </span>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="p-0 flex-grow-1 position-relative">
                  <div ref={mapContainerRef} className="w-100 h-100 position-absolute" />
                  {renderSvgTransformHandles()}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* GCP Management Panel */}
          <Row className="mt-3">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2.5 px-3">
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h6 className="mb-0 fw-semibold text-dark flex-grow-1">
                      <MapPin size={16} className="text-secondary me-1.5" />
                      Ground Control Points (GCPs): {gcpPoints.length} complete (Min 3 required)
                    </h6>
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-muted fs-xs d-flex align-items-center gap-1">
                        {activeTabPoint.x !== null ? (
                          <>
                            <span>{`✓ Step 1 Image: X:${activeTabPoint.x} Y:${activeTabPoint.y}`}</span>
                            <Button
                              size="sm"
                              variant="link"
                              className="text-danger p-0 ms-1 fs-xs text-decoration-none"
                              onClick={() => setActiveTabPoint({ x: null, y: null, lng: null, lat: null })}
                              title="Cancel pending point"
                            >
                              ✕
                            </Button>
                          </>
                        ) : (
                          "1. Click Image..."
                        )}
                      </span>
                      <span className="text-muted border-end" style={{ height: "14px" }} />
                      <span className="text-muted fs-xs me-2">
                        {activeTabPoint.lng !== null ? `✓ Step 2 Map: Lng:${activeTabPoint.lng} Lat:${activeTabPoint.lat}` : "2. Click Map..."}
                      </span>
                    </div>
                  </div>

                  {gcpPoints.length === 0 ? (
                    <p className="text-muted fs-xs italic text-center py-2 mb-0">
                      No Ground Control Points configured. Place control points above to overlay.
                    </p>
                  ) : (
                    <Table size="sm" bordered responsive hover striped className="mb-0 fs-xs align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "80px" }}>Point #</th>
                          <th>Pixel Coord (X, Y)</th>
                          <th>Geographic Coord (Lng, Lat)</th>
                          <th style={{ width: "130px" }}>Ground Residual</th>
                          <th style={{ width: "90px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gcpPoints.map((pt, idx) => (
                          <tr key={idx} className={pt.bad ? "table-danger" : ""}>
                            <td className="fw-semibold">
                              {idx + 1}
                              {pt.imported && (
                                <Badge bg="light" text="dark" className="border ms-1 fw-normal" title="From the import; moves with the sheet">
                                  imported
                                </Badge>
                              )}
                            </td>
                            <td>{`X: ${Math.round(pt.x * 10) / 10}px, Y: ${Math.round(pt.y * 10) / 10}px`}</td>
                            <td>{`${pt.lng.toFixed(6)}, ${pt.lat.toFixed(6)}`}</td>
                            <td>
                              {pt.res != null ? (
                                <span className={`badge ${pt.bad ? "bg-danger" : "bg-light text-dark border"}`}>
                                  {pt.res < 10 ? pt.res.toFixed(1) : Math.round(pt.res)} m {pt.bad ? "⚠️ High" : ""}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="py-0 px-1.5 fs-xs"
                                onClick={() => removeGcpPair(idx)}
                                disabled={isLocked}
                                title={isLocked ? "Unlock overlay position to delete point" : "Delete this control point pair"}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Precision Loupe 10x Magnifier Canvas */}
          <canvas
            ref={loupeCanvasRef}
            width={150}
            height={150}
            style={{
              position: "fixed",
              left: `${loupeState.clientX + 22}px`,
              top: `${loupeState.clientY - 168}px`,
              width: "150px",
              height: "150px",
              borderRadius: "50%",
              pointerEvents: "none",
              border: "2px solid #185fa5",
              boxShadow: "0 3px 14px rgba(0,0,0,0.35)",
              display: loupeState.show ? "block" : "none",
              zIndex: 9999,
              backgroundColor: "#ffffff",
            }}
          />
        </>
      )}

      {/* Interactive Trace Image & Boundary Cropping Modal */}
      <Modal show={showTraceModal} onHide={() => setShowTraceModal(false)} size="xl" centered backdrop="static">
        <Modal.Header closeButton className="py-2.5 px-3 d-flex align-items-center justify-content-between">
          <Modal.Title className="fs-sm fw-bold d-flex align-items-center gap-2 mb-0">
            <Scissors size={16} className="text-primary" />
            Trace Boundary & Crop Sheet (Click corners around map border)
          </Modal.Title>
          <div className="d-flex align-items-center gap-1 me-3">
            {/* Rotate Controls */}
            <span className="text-muted fs-xs me-1 fw-semibold">Rotate:</span>
            <Button
              size="sm"
              variant="outline-secondary"
              className="py-0 px-2 d-flex align-items-center gap-1"
              onClick={() => handleRotateTrace(-90)}
              title="Rotate 90° Counter-Clockwise"
            >
              <RotateCcw size={12} />
              -90°
            </Button>
            <Badge bg="light" text="dark" className="border px-2 py-1 fs-xs font-monospace">
              {traceRotation}°
            </Badge>
            <Button
              size="sm"
              variant="outline-secondary"
              className="py-0 px-2 d-flex align-items-center gap-1"
              onClick={() => handleRotateTrace(+90)}
              title="Rotate 90° Clockwise"
            >
              +90°
              <RotateCw size={12} />
            </Button>

            <div className="border-end mx-2" style={{ height: "20px" }} />

            {/* Zoom Controls */}
            <span className="text-muted fs-xs me-1 fw-semibold">Zoom:</span>
            <Button
              size="sm"
              variant="outline-secondary"
              className="py-0 px-2"
              onClick={handleTraceZoomOut}
              disabled={traceZoom <= 1}
              title="Zoom Out"
            >
              <ZoomOut size={14} />
            </Button>
            <Badge bg="light" text="dark" className="border px-2 py-1 fs-xs font-monospace">
              {Math.round(traceZoom * 100)}%
            </Badge>
            <Button
              size="sm"
              variant="outline-secondary"
              className="py-0 px-2"
              onClick={handleTraceZoomIn}
              disabled={traceZoom >= 4}
              title="Zoom In"
            >
              <ZoomIn size={14} />
            </Button>
            {traceZoom > 1 && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="py-0 px-1.5 fs-xs ms-1"
                onClick={handleTraceZoomReset}
                title="Reset Zoom to 100%"
              >
                Reset
              </Button>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-3 bg-dark text-center overflow-hidden d-flex flex-column align-items-center" style={{ height: "72vh" }}>
          <div className="d-flex align-items-center justify-content-between w-100 text-white-50 fs-xs mb-2 flex-shrink-0 px-1">
            <span>Click left mouse button sequentially around outer border to draw cropping polygon.</span>
            <span className="text-warning-emphasis fw-medium">
              💡 Hold Shift + Left-click & drag (or Middle-click) to pan. Wheel to zoom.
            </span>
          </div>
          <div
            ref={traceContainerRef}
            className="w-100 flex-grow-1 border rounded shadow-sm bg-white overflow-hidden position-relative"
            onMouseDown={handleTraceContainerMouseDown}
            onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
            style={{
              maxHeight: "calc(72vh - 40px)",
              userSelect: "none",
              cursor: isTracePanning ? "grabbing" : "default",
            }}
          >
            {(() => {
              const traceDims = getTraceDisplayDimensions();
              return (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    minWidth: "100%",
                    minHeight: "100%",
                    padding: "20px",
                    width: "max-content",
                    height: "max-content",
                    margin: "auto",
                  }}
                >
                  <canvas
                    ref={traceCanvasRef}
                    width={traceDims.canvasWidth}
                    height={traceDims.canvasHeight}
                    onClick={handleTraceCanvasClick}
                    style={{
                      cursor: isTracePanning ? "grabbing" : "crosshair",
                      display: "block",
                      width: `${traceDims.displayW}px`,
                      height: `${traceDims.displayH}px`,
                      maxWidth: "none",
                      maxHeight: "none",
                      boxShadow: traceZoom > 1 ? "0 4px 20px rgba(0,0,0,0.15)" : "none",
                      transition: isTracePanning ? "none" : "width 0.1s ease-out, height 0.1s ease-out",
                    }}
                  />
                </div>
              );
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted fs-xs me-2">
              Points: <b>{tracePoints.length}</b> (Min 3 required)
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setTracePoints((prev) => prev.slice(0, -1))}
              disabled={tracePoints.length === 0}
            >
              <RotateCcw size={12} className="me-1" />
              Undo Point
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setTracePoints([])}
              disabled={tracePoints.length === 0}
            >
              <X size={12} className="me-1" />
              Clear Points
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2">
            {customCroppedSrc && (
              <Button size="sm" variant="outline-warning" onClick={handleResetTraceMask}>
                Reset Original
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setShowTraceModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleApplyTraceMask}
              disabled={tracePoints.length === 0 && traceRotation === 0 ? true : tracePoints.length > 0 && tracePoints.length < 3}
              className="fw-semibold"
            >
              <Check size={14} className="me-1" />
              {tracePoints.length >= 3
                ? `Apply Crop${traceRotation !== 0 ? ` & Rotate (${traceRotation}°)` : " Mask"}`
                : `Apply Rotation (${traceRotation}°)`}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showStartOriginal} onHide={startingOriginal ? undefined : () => setShowStartOriginal(false)} centered>
        <Modal.Header closeButton={!startingOriginal} className="py-2.5 px-3">
          <Modal.Title className="fs-sm fw-bold mb-0">Start again from the original PDF?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-xs">
          <p className="mb-2">
            This sheet was saved from a cut-out of a low-resolution preview, which is why it looks soft on the map. This loads
            its original PDF instead, so you can crop it again and save it sharp.
          </p>
          <p className="mb-2">
            Your control points are moved onto the PDF automatically when the old cut-out can be found in it, so the sheet
            lands where it was. They follow your new crop; any outside it are removed. If the old cut-out can't be found, you
            will need to place the points again.
          </p>
          <p className="mb-0 text-muted">Nothing changes on the map until you save. Finding the old cut-out can take up to a minute.</p>
        </Modal.Body>
        <Modal.Footer className="py-2 px-3">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowStartOriginal(false)} disabled={startingOriginal}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleStartFromOriginal} disabled={startingOriginal}>
            {startingOriginal ? "Loading the PDF…" : "Load original PDF"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
